import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useFrappeGetCall, useFrappePostCall } from 'frappe-react-sdk';
import toast from 'react-hot-toast';
import { Button } from '../../components/ui/button';
import './CommentEnhancements.css';

import VideoList from '../../components/VideoList';
import CustomVideoPlayer from '../../components/CustomVideoPlayer';

import { ArrowLeft, MessageSquare, Clock, Video as VideoIcon, Download, Send, Eye, Calendar, Users, FileText, Mic, Zap, AlertCircle, CheckCircle, ChevronDown, ChevronUp, Info, Trash2, Edit3, Save, X, ClipboardCheck, Target, Maximize2, Columns, GraduationCap, Play, Square, RefreshCw, SkipForward, Crown, Sparkles, Brain, TrendingUp, Award, Quote } from 'lucide-react';

// Import types and utilities
import type {
  Video,
  Comment,
  CustomTemplate,
  VideoPlayerState,
  ActiveLabel,
  EvaluationData,
  SpeechRecognition,
  SpeechRecognitionEvent,
  SpeechRecognitionErrorEvent
} from './types/session.types';
import { formatTime, formatDate } from './utils/time.utils';
import { isEvaluationComment } from './utils/comment.utils';
import { getColorClasses, getEvaluationLabel, getStatusColor } from './utils/session.utils';
import { canDeleteComment, canEditComment } from './utils/permissions.utils';

// Import hooks and components
import { useSessionData } from './hooks/useSessionData';
import { VideoSelectionModal, AdditionalVideosPanel, TemplateManagerModal } from './components';
import { useSimpleLabelClassifier } from '../../hooks/useSimpleLabelClassifier';
import { SimpleLabelSuggestion } from '../../components/SimpleLabelSuggestion';
import { useISBARGuide } from '../../hooks/useISBARGuide';
// import { SessionHeader } from './components/Layout/SessionHeader'; // TODO: Use SessionHeader component in render


interface SessionDetailMainProps {
  sessionName: string | null;
  authUser: string | null;
}

export const SessionDetailMain: React.FC<SessionDetailMainProps> = ({ sessionName, authUser }) => {

  // All useState hooks must be called before any early returns or conditional logic
  const [currentVideo, setCurrentVideo] = useState<Video | null>(null);
  const [videoPlayerStates, setVideoPlayerStates] = useState<Map<string, VideoPlayerState>>(new Map());
  const [comments, setComments] = useState<Comment[]>([]);
  const [layout, setLayout] = useState<'single' | 'side-by-side'>('single');
  const [isExporting, setIsExporting] = useState(false);

  // Multi-video state management
  const [activeVideos, setActiveVideos] = useState<Video[]>([]); // Videos currently displayed in grid
  const [timelineVideo, setTimelineVideo] = useState<Video | null>(null); // Video used for comments/labels

  // Video synchronization state
  const [isPlayPauseSync, setIsPlayPauseSync] = useState<boolean>(true); // Sync play/pause across videos
  const [isSeekSync, setIsSeekSync] = useState<boolean>(false); // Sync seek/time across videos
  const [masterVideo, setMasterVideo] = useState<Video | null>(null); // Master video for sync
  // const maxVideosInGrid = 4; // Maximum videos that can fit (will be used in later tasks)
  const [isListening, setIsListening] = useState(false);
  const [speechRecognition, setSpeechRecognition] = useState<SpeechRecognition | null>(null);
  const [isVoiceSupported, setIsVoiceSupported] = useState(false);
  const [voiceTranscript, setVoiceTranscript] = useState('');
  const [microphonePermission, setMicrophonePermission] = useState<'prompt' | 'granted' | 'denied'>('prompt');
  const [showFullDescription, setShowFullDescription] = useState(false);
  const [showFloatingComment, setShowFloatingComment] = useState(false);
  const [floatingCommentTimestamp, setFloatingCommentTimestamp] = useState(0);
  const annotationDuration = 60; // Fixed value since duration mode was removed
  const [annotationCommentType, setAnnotationCommentType] = useState<'identification' | 'situation' | 'background_history' | 'background_examination' | 'assessment' | 'recommendation' | 'general'>('general');
  const [selectedCommentColor, setSelectedCommentColor] = useState('#3B82F6'); // Default blue color
  const [selectedStudentForComment, setSelectedStudentForComment] = useState<'none' | 'student1' | 'student2'>('none');

  // Local storage for comment colors (temporary solution until backend supports comment_color)
  const [commentColorMap, setCommentColorMap] = useState<Map<string, string>>(() => {
    try {
      const stored = localStorage.getItem('comment_colors');
      return stored ? new Map(Object.entries(JSON.parse(stored))) : new Map();
    } catch {
      return new Map();
    }
  });

  // Temporary storage for pending comments (until we get the real comment ID)
  const [pendingCommentColors, setPendingCommentColors] = useState<Map<string, { color: string, timestamp: number }>>(() => {
    try {
      const stored = localStorage.getItem('pending_comment_colors');
      if (stored) {
        const parsed = JSON.parse(stored);
        return new Map(Object.entries(parsed).map(([key, value]) => [key, value as { color: string, timestamp: number }]));
      }
      return new Map();
    } catch {
      return new Map();
    }
  });

  // Predefined color palette for comments - streamlined for consistency
  const commentColorOptions = [
    { name: 'Blue', value: '#3B82F6', bg: 'bg-blue-500' },
    { name: 'Green', value: '#10B981', bg: 'bg-green-500' },
    { name: 'Red', value: '#EF4444', bg: 'bg-red-500' },
    { name: 'Yellow', value: '#F59E0B', bg: 'bg-yellow-500' }
  ];

  // Color guide for consistent labeling
  const colorGuide = {
    'Blue': 'General observations, neutral comments',
    'Green': 'Positive feedback, excellent technique',
    'Red': 'Critical issues, safety concerns',
    'Yellow': 'Areas for improvement, caution needed'
  };

  // State for color guide modal
  const [showColorGuide, setShowColorGuide] = useState(false);

  // State for ISBAR guide modal
  const [showISBARGuide, setShowISBARGuide] = useState(false);

  // State for AI Recommendations panel
  const [showAIPanel, setShowAIPanel] = useState(false);
  const [aiRecommendations, setAiRecommendations] = useState<any>(null);
  const [isLoadingAI, setIsLoadingAI] = useState(false);

  // Function to handle CSRF errors with retry mechanism
  const handleCSRFError = async (retryFunction: () => Promise<any>, maxRetries = 1) => {
    try {
      // First, try to refresh the session data
      await refreshSession();

      // Wait a brief moment for the session to refresh
      await new Promise(resolve => setTimeout(resolve, 500));

      // Retry the original function
      if (maxRetries > 0) {
        return await retryFunction();
      }
    } catch (retryError) {
      console.error('Retry after session refresh failed:', retryError);
      throw retryError;
    }
  };


  // Persist comment color map to localStorage
  useEffect(() => {
    try {
      const colorMapObject = Object.fromEntries(commentColorMap.entries());
      localStorage.setItem('comment_colors', JSON.stringify(colorMapObject));
    } catch (error) {
      console.warn('Failed to persist comment colors:', error);
    }
  }, [commentColorMap]);

  // Persist pending comment colors to localStorage
  useEffect(() => {
    try {
      const pendingObject = Object.fromEntries(pendingCommentColors.entries());
      localStorage.setItem('pending_comment_colors', JSON.stringify(pendingObject));
    } catch (error) {
      console.warn('Failed to persist pending comment colors:', error);
    }
  }, [pendingCommentColors]);

  // Helper function to get comment color (checks local map first, then comment object, then pending)
  const getCommentDisplayColor = (comment: any): string => {
    // First check local storage map for this comment
    const localColor = commentColorMap.get(comment.name);
    if (localColor) {
      return localColor;
    }

    // Check pending comments by matching comment text and approximate timestamp
    const pendingKey = `${comment.comment_text}_${comment.video_title}`;
    const pending = pendingCommentColors.get(pendingKey);
    if (pending) {
      console.log(`🔄 Moving pending color to permanent storage for ${comment.name}: ${pending.color}`);
      // Move from pending to permanent storage
      setCommentColorMap(prev => new Map(prev.set(comment.name, pending.color)));
      setPendingCommentColors(prev => {
        const newMap = new Map(prev);
        newMap.delete(pendingKey);
        return newMap;
      });
      return pending.color;
    }

    // Then check if comment object has color (from backend)
    if (comment.comment_color && comment.comment_color !== '#FFFFFF') {
      return comment.comment_color;
    }

    // Default to white for fallback
    return '#FFFFFF';
  };

  const [commentToDelete, setCommentToDelete] = useState<{ name: string, text: string } | null>(null);
  const [editingComment, setEditingComment] = useState<{ name: string, text: string } | null>(null);
  const [editCommentText, setEditCommentText] = useState('');
  const [editCommentColor, setEditCommentColor] = useState('#3B82F6');
  const [editingDuration, setEditingDuration] = useState<string | null>(null);
  const [editDurationValue, setEditDurationValue] = useState('');
  const [editingStudentNames, setEditingStudentNames] = useState(false);
  const [editStudent1Name, setEditStudent1Name] = useState('');
  const [editStudent2Name, setEditStudent2Name] = useState('');
  const [customTemplates, setCustomTemplates] = useState<CustomTemplate[]>([]);
  const [showTemplateManager, setShowTemplateManager] = useState(false);
  const [newTemplate, setNewTemplate] = useState({
    title: '',
    content: '',
    color: 'blue',
    emoji: '💬'
  });
  const [editingTemplate, setEditingTemplate] = useState<CustomTemplate | null>(null);
  const [activeTab, setActiveTab] = useState<'comment' | 'evaluation'>('comment');
  const [evaluationData, setEvaluationData] = useState<EvaluationData>({
    identification: '',
    situation: '',
    history: '',
    examination: '',
    assessment: '',
    recommendation: '',
    grs: '',
    comment: ''
  });
  const [evaluationExpanded, setEvaluationExpanded] = useState<{ [key: string]: boolean }>({});
  const [activeLabels, setActiveLabels] = useState<ActiveLabel[]>([]);
  // const [labelMode, setLabelMode] = useState<'duration' | 'start_end'>('start_end'); // Removed - no longer needed
  const [expandedComments, setExpandedComments] = useState<Set<string>>(new Set());


  // ISBAR guide state
  const { shouldShowGuide, skipGuide } = useISBARGuide();
  const [labelStatusMessage, setLabelStatusMessage] = useState('');
  const [isbarValue, setIsbarValue] = useState('');
  const [showVideoSelection, setShowVideoSelection] = useState(false);
  const videoRefs = useRef<Map<string, HTMLVideoElement | null>>(new Map());
  const masterVideoRef = useRef<HTMLDivElement | null>(null);
  const [masterVideoHeight, setMasterVideoHeight] = useState<number>(0);

  // Simple AI color classifier
  const {
    suggestedColor,
    isClassifying,
    error: classificationError,
    classifyComment,
    clearSuggestion,
    aiEnabled,
    toggleAI,
    isTogglingAI
  } = useSimpleLabelClassifier();

  // Use the session data hook
  const {
    sessionData: hookSessionData,
    error: sessionError,
    hasAccess,
    isAccessPending,
    isLoading: isSessionLoading,
    refreshSession
  } = useSessionData({ sessionName, authUser });


  // Remove early return - handle this after all hooks

  // Use sessionData from hook directly instead of local state
  const sessionData = hookSessionData;
  const error = sessionError;

  // Show error if no sessionName is provided - after all hooks
  if (!sessionName) {
    return (
      <div className="flex flex-col items-center justify-center h-screen bg-gradient-to-br from-gray-50 via-blue-50/30 to-indigo-50/50">
        <div className="text-center space-y-4">
          <AlertCircle className="h-16 w-16 text-red-500 mx-auto" />
          <h2 className="text-2xl font-bold text-gray-900">Session Not Found</h2>
          <p className="text-gray-600">The session you're looking for doesn't exist or the URL is invalid.</p>
          <Link to="/">
            <Button className="mt-4">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Home
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  // Observe master video height changes for responsive additional videos
  useEffect(() => {
    if (!masterVideoRef.current) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { height } = entry.contentRect;
        setMasterVideoHeight(height);
      }
    });

    resizeObserver.observe(masterVideoRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, [masterVideo]);

  // Debug and remove unwanted spacer elements
  useEffect(() => {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === 1) { // Element node
            const element = node as HTMLElement;
            if (element.style.minHeight &&
              (element.classList.contains('h-0') || element.style.minHeight.includes('px'))) {
              console.warn('Spacer div detected and removed:', element);
              element.remove(); // Immediate removal
            }
          }
        });
      });
    });

    const container = document.querySelector('.comment-list-container');
    if (container) {
      observer.observe(container, { childList: true, subtree: true });
    }

    return () => observer.disconnect();
  }, [comments]); // Re-run when comments change





  // API for adding comments
  const { call: addComment, loading: isAddingComment } = useFrappePostCall(
    'surgical_training.api.comment.add_comment'
  );

  // API for deleting comments
  const { call: deleteComment, loading: isDeletingComment } = useFrappePostCall(
    'surgical_training.api.comment.delete_comment'
  );

  // API for updating comments
  const { call: updateComment } = useFrappePostCall(
    'surgical_training.api.comment.update_comment'
  );

  // API for updating session student names
  const { call: updateStudentNames } = useFrappePostCall(
    'surgical_training.api.session.update_session_student_names'
  );

  // API for custom templates
  const { data: templatesData, mutate: mutateTemplates, error: templatesError } = useFrappeGetCall(
    'surgical_training.api.comment.get_custom_templates'
  );

  const { call: createTemplate /*, loading: isCreatingTemplate*/ } = useFrappePostCall(
    'surgical_training.api.comment.create_custom_template'
  );

  const { call: updateTemplateAPI /*, loading: isUpdatingTemplate*/ } = useFrappePostCall(
    'surgical_training.api.comment.update_custom_template'
  );

  const { call: deleteTemplate /*, loading: isDeletingTemplate*/ } = useFrappePostCall(
    'surgical_training.api.comment.delete_custom_template'
  );

  // Initialize speech recognition
  useEffect(() => {
    const initializeSpeechRecognition = () => {
      // Check for browser support
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;

      if (SpeechRecognition) {
        const recognition = new SpeechRecognition();

        // Configure speech recognition
        recognition.continuous = true;
        recognition.interimResults = true;
        recognition.lang = 'en-US';
        recognition.maxAlternatives = 1;

        // Handle results
        recognition.onresult = (event: SpeechRecognitionEvent) => {
          let finalTranscript = '';
          let interimTranscript = '';

          for (let i = event.resultIndex; i < event.results.length; i++) {
            const transcript = event.results[i][0].transcript;
            if (event.results[i].isFinal) {
              finalTranscript += transcript;
            } else {
              interimTranscript += transcript;
            }
          }

          setVoiceTranscript(finalTranscript + interimTranscript);

          // Update the target video's comment with the transcript
          const targetVideo = getTargetVideoForComments();
          if (targetVideo && finalTranscript) {
            const currentComment = videoPlayerStates.get(targetVideo.title)?.newComment || '';
            const newComment = currentComment ? `${currentComment} ${finalTranscript}` : finalTranscript;
            handleCommentChange(targetVideo.title, newComment);
          }
        };

        // Handle errors
        recognition.onerror = (event: SpeechRecognitionErrorEvent) => {
          console.error('Speech recognition error:', event.error);
          setIsListening(false);

          switch (event.error) {
            case 'network':
              toast.error('Network error during speech recognition');
              break;
            case 'not-allowed':
              toast.error('Microphone access denied. Please allow microphone access and try again.');
              setMicrophonePermission('denied');
              break;
            case 'no-speech':
              toast('No speech detected. Try speaking closer to the microphone.');
              break;
            case 'audio-capture':
              toast.error('No microphone found or audio capture failed');
              break;
            default:
              toast.error(`Speech recognition error: ${event.error}`);
          }
        };

        // Handle start
        recognition.onstart = () => {
          setIsListening(true);
          setVoiceTranscript('');
          toast.success('🎤 Listening... Speak now');
        };

        // Handle end
        recognition.onend = () => {
          setIsListening(false);
          setVoiceTranscript('');
        };

        setSpeechRecognition(recognition);
        setIsVoiceSupported(true);
      } else {
        setIsVoiceSupported(false);
        console.warn('Speech recognition not supported in this browser');
      }
    };

    initializeSpeechRecognition();

    // Check microphone permission
    navigator.permissions.query({ name: 'microphone' as PermissionName })
      .then((result) => {
        setMicrophonePermission(result.state as 'prompt' | 'granted' | 'denied');

        result.onchange = () => {
          setMicrophonePermission(result.state as 'prompt' | 'granted' | 'denied');
        };
      })
      .catch(() => {
        // Permissions API not supported, assume prompt
        setMicrophonePermission('prompt');
      });
  }, [currentVideo, videoPlayerStates]);

  // Voice recording functionality
  const toggleVoiceRecording = async () => {
    if (!isVoiceSupported) {
      toast.error('Speech recognition is not supported in your browser. Please use Chrome, Edge, or Safari.');
      return;
    }

    if (!speechRecognition) {
      toast.error('Speech recognition not initialized');
      return;
    }

    if (isListening) {
      // Stop recording
      speechRecognition.stop();
      setIsListening(false);
      toast('🛑 Voice recording stopped');
    } else {
      try {
        // Request microphone permission if needed
        if (microphonePermission !== 'granted') {
          try {
            await navigator.mediaDevices.getUserMedia({ audio: true });
            setMicrophonePermission('granted');
          } catch (error) {
            toast.error('Microphone access required for voice comments. Please allow access and try again.');
            setMicrophonePermission('denied');
            return;
          }
        }

        // Start recording
        speechRecognition.start();
      } catch (error) {
        console.error('Error starting speech recognition:', error);
        toast.error('Failed to start voice recording. Please check your microphone and try again.');
      }
    }
  };

  // Clear voice transcript
  const clearVoiceTranscript = () => {
    setVoiceTranscript('');
    if (speechRecognition && isListening) {
      speechRecognition.stop();
    }
  };

  // Insert voice transcript into comment
  const insertVoiceTranscript = () => {
    const targetVideo = getTargetVideoForComments();
    if (targetVideo && voiceTranscript.trim()) {
      const currentComment = videoPlayerStates.get(targetVideo.title)?.newComment || '';
      const newComment = currentComment ? `${currentComment} ${voiceTranscript.trim()}` : voiceTranscript.trim();
      handleCommentChange(targetVideo.title, newComment);
      setVoiceTranscript('');
      toast.success('Voice transcript added to comment');
    }
  };

  // Keyboard shortcuts for better UX
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      // Only trigger if not typing in an input/textarea
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;

      switch (e.key.toLowerCase()) {
        case 'c':
          // Don't trigger if Ctrl+C is pressed (user wants to copy)
          if (e.ctrlKey || e.metaKey) return;

          e.preventDefault();
          // Focus on the comment textarea
          const textarea = document.querySelector('textarea[placeholder*="Describe what you observe"]') as HTMLTextAreaElement;
          if (textarea) {
            textarea.focus();
          }
          break;
        case 'e':
          // End the most recent active label for target video
          const targetVideo = getTargetVideoForComments();
          if (targetVideo) {
            e.preventDefault();
            const activeLabelsForVideo = getActiveLabelsForVideo(targetVideo.title);
            if (activeLabelsForVideo.length > 0) {
              const mostRecentLabel = activeLabelsForVideo[activeLabelsForVideo.length - 1];
              handleEndLabel(mostRecentLabel.id);
            }
          }
          break;
        case 'enter':
          // Submit comment when Enter is pressed (with Ctrl/Cmd)
          if (e.ctrlKey || e.metaKey) {
            e.preventDefault();
            const targetVideo = getTargetVideoForComments();
            if (targetVideo) {
              const comment = videoPlayerStates.get(targetVideo.title)?.newComment.trim();
              if (comment) {
                handleStartLabel(targetVideo.title, comment, annotationCommentType);
              }
            }
          }
          break;
        case ' ':
          const targetVideoForPlayPause = getTargetVideoForComments();
          if (targetVideoForPlayPause) {
            e.preventDefault();
            const videoState = videoPlayerStates.get(targetVideoForPlayPause.title);
            if (videoState) {
              handlePlayPause(targetVideoForPlayPause.title, !videoState.isPlaying);
            }
          }
          break;
        case 'escape':
          // Clear comment input
          const targetVideoForEscape = getTargetVideoForComments();
          if (targetVideoForEscape) {
            handleCommentChange(targetVideoForEscape.title, '');
          }
          break;
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [currentVideo, videoPlayerStates, activeLabels, annotationCommentType]);

  useEffect(() => {
    if (sessionData) {

      // Process videos
      if (sessionData.videos && Array.isArray(sessionData.videos)) {
        // Videos processed
      }

      setComments(Array.isArray(sessionData.comments) ? sessionData.comments : []);

      // Set first video as current if available, but preserve current video if it exists
      if (sessionData.videos && Array.isArray(sessionData.videos) && sessionData.videos.length > 0) {
        // Only set to first video if no current video is set, or if current video is not in the new data
        if (!currentVideo || !sessionData.videos.find((v: Video) => v.title === currentVideo.title)) {
          setCurrentVideo(sessionData.videos[0]);
        }

        // Initialize player states for all videos, preserving existing states
        setVideoPlayerStates(prevStates => {
          const newStates = new Map<string, VideoPlayerState>();
          sessionData.videos.forEach((video: Video) => {
            const existingState = prevStates.get(video.title);
            newStates.set(video.title, {
              isPlaying: existingState?.isPlaying || false,
              currentTime: existingState?.currentTime || 0,
              newComment: existingState?.newComment || ''
            });
          });
          return newStates;
        });
      }
    }

    if (error) {
      console.error('Session data error from hook:', error);
    }
  }, [sessionData, error, currentVideo]);

  // Load custom templates
  useEffect(() => {
    if (templatesError) {
      console.warn('Error loading custom templates:', templatesError);
      setCustomTemplates([]); // Fallback to empty array on error
      return;
    }

    if (templatesData) {
      // Handle different response structures
      if (Array.isArray(templatesData)) {
        setCustomTemplates(templatesData);
      } else if (templatesData.message && Array.isArray(templatesData.message)) {
        setCustomTemplates(templatesData.message);
      } else if (templatesData.data && Array.isArray(templatesData.data)) {
        setCustomTemplates(templatesData.data);
      } else {
        console.warn('Unexpected templates data structure:', templatesData);
        setCustomTemplates([]); // Fallback to empty array
      }
    }
  }, [templatesData, templatesError]);

  const handleProgress = (videoTitle: string, state: { playedSeconds: number }) => {
    setVideoPlayerStates(prevStates => {
      const newStates = new Map(prevStates);
      const currentState = newStates.get(videoTitle) || {
        isPlaying: false,
        currentTime: 0,
        newComment: ''
      };

      newStates.set(videoTitle, {
        ...currentState,
        currentTime: state.playedSeconds
      });

      return newStates;
    });
  };

  // Handle seek events from clicking on the timeline
  const handleSeek = (videoTitle: string, seconds: number) => {
    setVideoPlayerStates(prevStates => {
      const newStates = new Map(prevStates);
      const currentState = newStates.get(videoTitle) || {
        isPlaying: false,
        currentTime: 0,
        newComment: ''
      };

      newStates.set(videoTitle, {
        ...currentState,
        currentTime: seconds
      });

      return newStates;
    });
  };

  const handlePlayPause = (videoTitle: string, playing: boolean, skipSync: boolean = false) => {
    setVideoPlayerStates(prevStates => {
      const newStates = new Map(prevStates);
      const currentState = newStates.get(videoTitle) || {
        isPlaying: false,
        currentTime: 0,
        newComment: ''
      };

      newStates.set(videoTitle, {
        ...currentState,
        isPlaying: playing
      });

      // Sync play/pause across other videos in side-by-side mode
      if (layout === 'side-by-side' && isPlayPauseSync && !skipSync && activeVideos.length > 1) {
        activeVideos.forEach(video => {
          if (video.title !== videoTitle) {
            const videoState = newStates.get(video.title) || {
              isPlaying: false,
              currentTime: 0,
              newComment: ''
            };
            newStates.set(video.title, {
              ...videoState,
              isPlaying: playing
            });
          }
        });
      }

      return newStates;
    });
  };

  // Handle time synchronization across videos (will be used when integrating with video player)
  // const handleTimeSync = (videoTitle: string, newTime: number, skipSync: boolean = false) => {
  //   setVideoPlayerStates(prevStates => {
  //     const newStates = new Map(prevStates);
  //     const currentState = newStates.get(videoTitle) || {
  //       isPlaying: false,
  //       currentTime: 0,
  //       newComment: ''
  //     };
  //     
  //     newStates.set(videoTitle, {
  //       ...currentState,
  //       currentTime: newTime
  //     });
  //     
  //     // Sync time across other videos in side-by-side mode if seek sync is enabled
  //     if (layout === 'side-by-side' && isSeekSync && !skipSync && activeVideos.length > 1) {
  //       activeVideos.forEach(video => {
  //         if (video.title !== videoTitle) {
  //           const videoState = newStates.get(video.title) || {
  //             isPlaying: false,
  //             currentTime: 0,
  //             newComment: ''
  //           };
  //           newStates.set(video.title, {
  //             ...videoState,
  //             currentTime: newTime
  //           });
  //         }
  //       });
  //     }
  //     
  //     return newStates;
  //   });
  // };

  const handleCommentChange = (videoTitle: string, comment: string) => {
    setVideoPlayerStates(prevStates => {
      const newStates = new Map(prevStates);
      const currentState = newStates.get(videoTitle) || {
        isPlaying: false,
        currentTime: 0,
        newComment: ''
      };

      newStates.set(videoTitle, {
        ...currentState,
        newComment: comment
      });

      return newStates;
    });
  };

  const handleVideoChange = (video: Video) => {
    setCurrentVideo(video);
    setLayout('single');
  };





  // New label system functions
  const handleStartLabel = (videoTitle: string, comment: string, type: string = 'positive') => {
    const targetVideo = getTargetVideoForComments();
    if (!targetVideo) return;

    const videoState = videoPlayerStates.get(videoTitle);
    if (!videoState) return;

    const newLabel: ActiveLabel = {
      id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      videoTitle,
      startTime: videoState.currentTime,
      comment,
      type,
      createdAt: Date.now()
    };

    setActiveLabels(prev => [...prev, newLabel]);
    toast.success(`Label started at ${formatTime(videoState.currentTime)}`);
    setLabelStatusMessage(`Label recording started at ${formatTime(videoState.currentTime)}`);

    // Clear the comment input
    handleCommentChange(videoTitle, '');
  };

  const handleEndLabel = async (labelId: string) => {
    const targetVideo = getTargetVideoForComments();
    if (!targetVideo) return;

    const videoState = videoPlayerStates.get(targetVideo.title);
    if (!videoState) return;

    const activeLabel = activeLabels.find(label => label.id === labelId);
    if (!activeLabel) return;

    const endTime = videoState.currentTime;
    const duration = Math.round(endTime - activeLabel.startTime);

    if (duration <= 0) {
      toast.error('End time must be after start time');
      return;
    }

    // Clear AI suggestion when stopping a label
    clearSuggestion();

    // Capture the current comment from the input field
    const currentComment = videoPlayerStates.get(targetVideo.title)?.newComment || activeLabel.comment;

    // Validate that comment is not empty
    if (!currentComment || currentComment.trim() === '') {
      toast.error('Please enter a comment before stopping the label');
      return;
    }

    try {
      const sessionReference = getSessionTimelineReference();
      if (!sessionReference) {
        toast.error('No session video reference available');
        return;
      }

      // Create the comment with calculated duration
      const commentData = {
        session: sessionName,
        video_title: sessionReference.title,
        timestamp: activeLabel.startTime,
        comment_text: currentComment,
        duration: duration,
        comment_type: activeLabel.type,
        comment_color: selectedCommentColor // Add the selected color
      };

      const response = await addComment(commentData);

      if (response && response.message) {
        const responseData = response.message;

        if (responseData.message === 'Success') {
          toast.success(`Label completed: ${formatTime(duration)} duration`);
          setLabelStatusMessage(`Label completed with ${formatTime(duration)} duration`);

          // Store the comment color in local map if we have a comment name, otherwise store as pending
          const commentName = responseData.data?.name;
          if (commentName) {
            console.log(`💾 Storing color for label comment ${commentName}: ${selectedCommentColor}`);
            setCommentColorMap(prev => new Map(prev.set(commentName, selectedCommentColor)));
          } else {
            // Store as pending comment with text+video as key
            const pendingKey = `${currentComment}_${activeLabel.videoTitle}`;
            console.log(`💾 Storing pending color for label ${pendingKey}: ${selectedCommentColor}`);
            setPendingCommentColors(prev => new Map(prev.set(pendingKey, {
              color: selectedCommentColor,
              timestamp: Date.now()
            })));
          }

          // Remove the active label
          setActiveLabels(prev => prev.filter(label => label.id !== labelId));

          // Clear the comment input
          handleCommentChange(targetVideo.title, '');

          // Refresh comments
          await refreshSession();
        } else if (responseData.error) {
          toast.error(`Error: ${responseData.error}`);
        }
      }
    } catch (error) {
      console.error('Error completing label:', error);

      // Handle CSRF token errors specifically with retry
      if (error && typeof error === 'object' && error !== null) {
        const errorObj = error as any;
        if (errorObj.exc_type === 'CSRFTokenError' || errorObj.exception?.includes('CSRFTokenError')) {
          toast.loading('Session expired, attempting to refresh...', { id: 'csrf-retry' });

          try {
            // Retry the operation after refreshing session
            await handleCSRFError(async () => {
              const retrySessionReference = getSessionTimelineReference();
              if (!retrySessionReference) {
                throw new Error('No session video reference available');
              }

              const commentData = {
                session: sessionName,
                video_title: retrySessionReference.title,
                timestamp: activeLabel.startTime,
                comment_text: currentComment,
                duration: duration,
                comment_type: activeLabel.type,
                comment_color: selectedCommentColor
              };

              const retryResponse = await addComment(commentData);

              if (retryResponse && retryResponse.message && retryResponse.message.message === 'Success') {
                toast.success(`Label completed: ${formatTime(duration)} duration`, { id: 'csrf-retry' });
                setLabelStatusMessage(`Label completed with ${formatTime(duration)} duration`);

                // Store the comment color and clean up
                const responseData = retryResponse.message;
                const commentName = responseData.data?.name;
                if (commentName) {
                  setCommentColorMap(prev => new Map(prev.set(commentName, selectedCommentColor)));
                }

                setActiveLabels(prev => prev.filter(label => label.id !== labelId));

                // Clear the comment input
                handleCommentChange(targetVideo.title, '');

                await refreshSession();
              } else {
                throw new Error('Retry failed');
              }
            });
          } catch (retryError) {
            toast.error('Session refresh failed. Please refresh the page manually.', {
              id: 'csrf-retry',
              duration: 5000
            });
          }
          return;
        }
        if (errorObj.httpStatus === 403 || errorObj.httpStatus === 401) {
          toast.error('Authentication failed. Please refresh the page and log in again.', {
            duration: 5000,
            id: 'auth-error'
          });
          return;
        }
      }

      toast.error('Failed to complete label. Please try again.');
    }
  };

  const handleCancelLabel = (labelId: string) => {
    setActiveLabels(prev => prev.filter(label => label.id !== labelId));
    toast('Label cancelled');
  };

  const getActiveLabelsForVideo = (videoTitle: string) => {
    return activeLabels.filter(label => label.videoTitle === videoTitle);
  };

  const handleAddComment = async (videoTitle: string, customTimestamp?: number, customDuration?: number, customCommentType?: string) => {
    const videoState = videoPlayerStates.get(videoTitle);
    if (!videoState || !videoState.newComment.trim()) return;

    try {
      // Store current time before adding comment
      const currentTime = customTimestamp !== undefined ? customTimestamp : videoState.currentTime;

      // Pause the video
      handlePlayPause(videoTitle, false);

      // Get student name if selected
      let studentName = null;
      if (selectedStudentForComment === 'student1') {
        studentName = sessionData?.session.student_1_name || 'Student 1';
      } else if (selectedStudentForComment === 'student2') {
        studentName = sessionData?.session.student_2_name || 'Student 2';
      }

      const sessionReference = getSessionTimelineReference();
      if (!sessionReference) {
        toast.error('No session video reference available');
        return;
      }

      // Prepare comment data with duration and type if provided
      const commentData: any = {
        session: sessionName,
        video_title: sessionReference.title,
        timestamp: currentTime,
        comment_text: videoState.newComment,
        comment_color: selectedCommentColor, // Add the selected color
        student_name: studentName // Add student name if selected
      };

      // Debug: Log what we're sending to backend
      console.log('🚀 Sending comment data to backend:', {
        commentData,
        selectedStudentForComment,
        studentName,
        sessionData_students: {
          student_1_name: sessionData?.session.student_1_name,
          student_2_name: sessionData?.session.student_2_name
        }
      });

      // Add duration if provided
      if (customDuration !== undefined) {
        commentData.duration = customDuration;
      }

      // Add comment type if provided (not auto-detect)
      if (customCommentType && customCommentType !== 'auto') {
        commentData.comment_type = customCommentType;
      }

      const response = await addComment(commentData);

      if (response && response.message) {
        const responseData = response.message;
        console.log('📥 Comment creation response:', responseData);

        if (responseData.message === 'Success') {
          toast.success('Comment added successfully');

          // Store the comment color in local map if we have a comment name, otherwise store as pending
          const commentName = responseData.data?.name;
          if (commentName) {
            console.log(`💾 Storing color for comment ${commentName}: ${selectedCommentColor}`);
            setCommentColorMap(prev => new Map(prev.set(commentName, selectedCommentColor)));
          } else {
            // Store as pending comment with text+video as key
            const pendingKey = `${videoState.newComment}_${videoTitle}`;
            console.log(`💾 Storing pending color for ${pendingKey}: ${selectedCommentColor}`);
            setPendingCommentColors(prev => new Map(prev.set(pendingKey, {
              color: selectedCommentColor,
              timestamp: Date.now()
            })));
          }

          // Clear comment input
          handleCommentChange(videoTitle, '');
          // Refresh comments
          await refreshSession();

          // Restore video position after comment is added
          setVideoPlayerStates(prevStates => {
            const newStates = new Map(prevStates);
            const currentState = newStates.get(videoTitle) || {
              isPlaying: false,
              currentTime: 0,
              newComment: ''
            };

            newStates.set(videoTitle, {
              ...currentState,
              currentTime: currentTime
            });

            return newStates;
          });

          // Update video position
          const videoRef = videoRefs.current.get(videoTitle);
          if (videoRef) {
            videoRef.currentTime = currentTime;
          }
        } else if (responseData.error) {
          toast.error(`Error: ${responseData.error}`);
          console.error('API Error:', responseData.error);
        }
      } else {
        toast.error('Failed to add comment: Invalid response');
        console.error('Invalid API response:', response);
      }
    } catch (error) {
      console.error('Error adding comment:', error);

      // Handle CSRF token errors specifically with retry
      if (error && typeof error === 'object' && error !== null) {
        const errorObj = error as any;
        if (errorObj.exc_type === 'CSRFTokenError' || errorObj.exception?.includes('CSRFTokenError')) {
          toast.loading('Session expired, attempting to refresh...', { id: 'csrf-retry-comment' });

          try {
            // Retry the operation after refreshing session
            await handleCSRFError(async () => {
              const retrySessionReference = getSessionTimelineReference();
              if (!retrySessionReference) {
                throw new Error('No session video reference available');
              }

              // Get student name if selected
              let studentName = null;
              if (selectedStudentForComment === 'student1') {
                studentName = sessionData?.session.student_1_name || 'Student 1';
              } else if (selectedStudentForComment === 'student2') {
                studentName = sessionData?.session.student_2_name || 'Student 2';
              }

              const commentData: any = {
                session: sessionName,
                video_title: retrySessionReference.title,
                timestamp: customTimestamp !== undefined ? customTimestamp : videoState.currentTime,
                comment_text: videoState.newComment,
                comment_color: selectedCommentColor,
                student_name: studentName
              };

              if (customDuration !== undefined) {
                commentData.duration = customDuration;
              }

              if (customCommentType && customCommentType !== 'auto') {
                commentData.comment_type = customCommentType;
              }

              const retryResponse = await addComment(commentData);

              if (retryResponse && retryResponse.message && retryResponse.message.message === 'Success') {
                toast.success('Comment added successfully', { id: 'csrf-retry-comment' });

                // Store the comment color and clean up
                const responseData = retryResponse.message;
                const commentName = responseData.data?.name;
                if (commentName) {
                  setCommentColorMap(prev => new Map(prev.set(commentName, selectedCommentColor)));
                }

                handleCommentChange(videoTitle, '');
                await refreshSession();
              } else {
                throw new Error('Retry failed');
              }
            });
          } catch (retryError) {
            toast.error('Session refresh failed. Please refresh the page manually.', {
              id: 'csrf-retry-comment',
              duration: 5000
            });
          }
          return;
        }
        if (errorObj.httpStatus === 403 || errorObj.httpStatus === 401) {
          toast.error('Authentication failed. Please refresh the page and log in again.', {
            duration: 5000,
            id: 'auth-error'
          });
          return;
        }
      }

      toast.error('Failed to add comment. Please try again.');
    }
  };

  const handleDeleteComment = async (commentName: string, _commentText: string) => {
    // Store current time before deleting
    let currentTime = 0;
    if (currentVideo) {
      const videoState = videoPlayerStates.get(currentVideo.title);
      if (videoState) currentTime = videoState.currentTime;
    }
    try {
      const response = await deleteComment({
        comment_name: commentName
      });
      if (response && response.message) {
        const responseData = response.message;
        if (responseData.message === 'Success') {
          toast.success('Comment deleted successfully');
          // Refresh comments
          await refreshSession();
          // Restore video position after comment is deleted
          if (currentVideo) {
            setVideoPlayerStates(prevStates => {
              const newStates = new Map(prevStates);
              const currentState = newStates.get(currentVideo.title) || {
                isPlaying: false,
                currentTime: 0,
                newComment: ''
              };
              newStates.set(currentVideo.title, {
                ...currentState,
                currentTime: currentTime
              });
              return newStates;
            });
            const videoRef = videoRefs.current.get(currentVideo.title);
            if (videoRef) {
              videoRef.currentTime = currentTime;
            }
          }
          setCommentToDelete(null);
        } else if (responseData.error) {
          toast.error(`Error: ${responseData.error}`);
          console.error('API Error:', responseData.error);
        }
      } else {
        toast.error('Failed to delete comment: Invalid response');
        console.error('Invalid API response:', response);
      }
    } catch (error) {
      console.error('Error deleting comment:', error);
      toast.error('Failed to delete comment');
    }
  };

  const handleEditComment = (comment: Comment) => {
    // Cancel any duration editing
    setEditingDuration(null);
    setEditDurationValue('');

    setEditingComment({ name: comment.name, text: comment.comment_text });
    setEditCommentText(comment.comment_text);

    // Set the current color or default
    const currentColor = getCommentDisplayColor(comment);
    setEditCommentColor(currentColor);
  };

  // Handle duration editing
  const handleEditDuration = (comment: Comment) => {
    // Cancel any other editing
    setEditingComment(null);
    setEditCommentText('');

    setEditingDuration(comment.name);
    setEditDurationValue(comment.duration ? comment.duration.toString() : '30');
  };

  const handleSaveDuration = async (commentName: string) => {
    if (!editDurationValue) return;

    const durationSeconds = parseInt(editDurationValue);
    if (isNaN(durationSeconds) || durationSeconds <= 0) {
      toast.error('Please enter a valid duration in seconds');
      return;
    }

    try {
      const response = await fetch('/api/method/surgical_training.api.comment.update_comment', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          comment_name: commentName,
          duration: durationSeconds
        }),
      });

      const result = await response.json();

      if (result && result.message && result.message.message === 'Success') {
        toast.success('Duration updated successfully');
        setEditingDuration(null);
        setEditDurationValue('');
        await refreshSession();
      } else {
        toast.error('Failed to update duration');
      }
    } catch (error) {
      console.error('Error updating duration:', error);
      toast.error('Failed to update duration');
    }
  };

  const handleCancelDurationEdit = () => {
    setEditingDuration(null);
    setEditDurationValue('');
  };

  const handleSaveComment = async () => {
    if (!editingComment) return;
    // Store current time before editing
    let currentTime = 0;
    if (currentVideo) {
      const videoState = videoPlayerStates.get(currentVideo.title);
      if (videoState) currentTime = videoState.currentTime;
    }
    try {
      const response = await updateComment({
        comment_name: editingComment.name,
        comment_text: editCommentText,
        comment_color: editCommentColor
      });
      if (response && response.message) {
        const responseData = response.message;
        if (responseData.message === 'Success') {
          toast.success('Comment updated successfully');
          // Refresh comments
          await refreshSession();
          // Update color in storage
          const updatedColorMap = new Map(commentColorMap);
          updatedColorMap.set(editingComment.name, editCommentColor);
          setCommentColorMap(updatedColorMap);
          localStorage.setItem('comment_colors', JSON.stringify(Object.fromEntries(updatedColorMap)));

          // Also update pending colors if needed
          const key = `${editCommentText}_${currentVideo?.title || ''}`;
          const updatedPending = new Map(pendingCommentColors);
          updatedPending.set(key, { color: editCommentColor, timestamp: Date.now() });
          setPendingCommentColors(updatedPending);
          localStorage.setItem('pending_comment_colors', JSON.stringify(Object.fromEntries(updatedPending)));

          // Clear editing state
          setEditingComment(null);
          setEditCommentText('');
          setEditCommentColor('#3B82F6');
          // Restore video position after comment is edited
          if (currentVideo) {
            setVideoPlayerStates(prevStates => {
              const newStates = new Map(prevStates);
              const currentState = newStates.get(currentVideo.title) || {
                isPlaying: false,
                currentTime: 0,
                newComment: ''
              };
              newStates.set(currentVideo.title, {
                ...currentState,
                currentTime: currentTime
              });
              return newStates;
            });
            const videoRef = videoRefs.current.get(currentVideo.title);
            if (videoRef) {
              videoRef.currentTime = currentTime;
            }
          }
        } else if (responseData.error) {
          toast.error(`Error: ${responseData.error}`);
          console.error('API Error:', responseData.error);
        }
      } else {
        toast.error('Failed to update comment: Invalid response');
        console.error('Invalid API response:', response);
      }
    } catch (error) {
      console.error('Error updating comment:', error);
      toast.error('Failed to update comment');
    }
  };

  const handleCancelEdit = () => {
    setEditingComment(null);
    setEditCommentText('');
    setEditCommentColor('#3B82F6');
  };

  // Student names editing functions
  const handleEditStudentNames = () => {
    setEditStudent1Name(sessionData?.session.student_1_name || '');
    setEditStudent2Name(sessionData?.session.student_2_name || '');
    setEditingStudentNames(true);
  };

  const handleSaveStudentNames = async () => {
    if (!sessionName) return;

    try {
      const response = await updateStudentNames({
        session_name: sessionName,
        student_1_name: editStudent1Name,
        student_2_name: editStudent2Name
      });

      if (response && response.message && response.message.message === 'Success') {
        toast.success('Student names updated successfully');
        await refreshSession(); // Refresh to get latest data
        setEditingStudentNames(false);
      } else {
        toast.error('Failed to update student names');
      }
    } catch (error) {
      console.error('Error updating student names:', error);
      toast.error('Failed to update student names');
    }
  };

  const handleCancelStudentNamesEdit = () => {
    setEditingStudentNames(false);
    setEditStudent1Name('');
    setEditStudent2Name('');
  };



  // Custom Template Management Functions
  const handleCreateTemplate = async () => {
    if (!newTemplate.title.trim() || !newTemplate.content.trim()) {
      toast.error('Please fill in both title and content');
      return;
    }

    try {
      await createTemplate({
        title: newTemplate.title.trim(),
        content: newTemplate.content.trim(),
        color: newTemplate.color,
        emoji: newTemplate.emoji
      });

      toast.success('Template created successfully!');
      setNewTemplate({ title: '', content: '', color: 'blue', emoji: '💬' });
      mutateTemplates(); // Refresh templates
    } catch (error) {
      console.error('Error creating template:', error);
      if (error && typeof error === 'object' && 'message' in error) {
        toast.error(`Failed to create template: ${error.message}`);
      } else {
        toast.error('Failed to create template. The template feature may not be available yet.');
      }
    }
  };

  const handleUpdateTemplate = async () => {
    if (!editingTemplate || !editingTemplate.title.trim() || !editingTemplate.content.trim()) {
      toast.error('Please fill in both title and content');
      return;
    }

    try {
      await updateTemplateAPI({
        template_name: editingTemplate.name,
        title: editingTemplate.title.trim(),
        content: editingTemplate.content.trim(),
        color: editingTemplate.color,
        emoji: editingTemplate.emoji
      });

      toast.success('Template updated successfully!');
      setEditingTemplate(null);
      mutateTemplates(); // Refresh templates
    } catch (error) {
      toast.error('Failed to update template');
      console.error('Error updating template:', error);
    }
  };

  const handleDeleteTemplate = async (templateName: string) => {
    if (!confirm('Are you sure you want to delete this template?')) {
      return;
    }

    try {
      await deleteTemplate({ template_name: templateName });
      toast.success('Template deleted successfully!');
      mutateTemplates(); // Refresh templates
    } catch (error) {
      toast.error('Failed to delete template');
      console.error('Error deleting template:', error);
    }
  };

  const handleEditTemplate = (template: CustomTemplate) => {
    setEditingTemplate({ ...template });
  };

  const handleCancelTemplateEdit = () => {
    setEditingTemplate(null);
  };

  // Evaluation handling functions
  const handleEvaluationChange = (field: keyof EvaluationData, value: string) => {
    setEvaluationData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const toggleEvaluationExpanded = (category: string) => {
    setEvaluationExpanded(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  const resetEvaluationForm = () => {
    setEvaluationData({
      identification: '',
      situation: '',
      history: '',
      examination: '',
      assessment: '',
      recommendation: '',
      grs: '',
      comment: ''
    });
    setEvaluationExpanded({});
  };


  const handleAddEvaluation = async () => {
    if (!currentVideo) return;

    // Check if at least one evaluation field is filled
    const evaluationFields = ['identification', 'situation', 'history', 'examination', 'assessment', 'recommendation', 'grs'];
    const filledFields = evaluationFields.filter(field => evaluationData[field as keyof EvaluationData]);

    if (filledFields.length === 0) {
      toast.error('Please complete at least one evaluation criteria');
      return;
    }

    try {
      // Calculate total score only for filled fields
      const filledScores = evaluationFields
        .filter(field => evaluationData[field as keyof EvaluationData])
        .map(field => parseInt(evaluationData[field as keyof EvaluationData]));
      const totalScore = filledScores.reduce((sum, score) => sum + score, 0);
      const maxPossibleScore = filledFields.length * 3;

      // Create a concise evaluation summary for comment text with special marker
      const evaluationLines = [
        '[EVALUATION]', // Special marker for timeline identification
        `📊 PERFORMANCE EVALUATION - ${sessionName}`,
        `⏰ Timestamp: ${formatTime(floatingCommentTimestamp)}`,
        '',
        '📋 COMPLETED ASSESSMENTS:'
      ];

      // Add only filled evaluation fields
      evaluationFields.forEach(field => {
        const value = evaluationData[field as keyof EvaluationData];
        if (value) {
          const fieldNames = {
            identification: 'Identification',
            situation: 'Situation',
            history: 'History',
            examination: 'Examination',
            assessment: 'Assessment',
            recommendation: 'Recommendation',
            grs: 'Global Rating'
          };
          evaluationLines.push(`• ${fieldNames[field as keyof typeof fieldNames]}: ${value}/3 (${getEvaluationLabel(field, value)})`);
        }
      });

      evaluationLines.push('');
      evaluationLines.push(`📈 PARTIAL SCORE: ${totalScore}/${maxPossibleScore} (${filledFields.length}/${evaluationFields.length} criteria completed)`);

      if (evaluationData.comment) {
        evaluationLines.push('');
        evaluationLines.push(`💬 ADDITIONAL NOTES: ${evaluationData.comment}`);
      }

      const evaluationSummary = evaluationLines.join('\n');

      // Check if the summary is too long and truncate if necessary
      const maxLength = 2000; // Reasonable limit for comment text
      const finalSummary = evaluationSummary.length > maxLength
        ? evaluationSummary.substring(0, maxLength - 50) + '... [TRUNCATED]'
        : evaluationSummary;

      // Store current time before adding evaluation
      const currentTime = floatingCommentTimestamp;

      const targetVideo = getTargetVideoForComments();
      if (!targetVideo) {
        toast.error('No target video selected for evaluation');
        return;
      }

      const sessionReference = getSessionTimelineReference();
      if (!sessionReference) {
        toast.error('No session video reference available');
        return;
      }

      const response = await addComment({
        session: sessionName,
        video_title: sessionReference.title,
        timestamp: currentTime,
        comment_text: finalSummary
      });

      if (response && response.message) {
        const responseData = response.message;

        if (responseData.message === 'Success') {
          toast.success('Evaluation added successfully');
          // Reset form and close modal
          resetEvaluationForm();
          setShowFloatingComment(false);
          // Refresh comments
          await refreshSession();
        } else if (responseData.error) {
          toast.error(`Error: ${responseData.error}`);
          console.error('API Error:', responseData.error);
        }
      } else {
        toast.error('Failed to add evaluation: Invalid response');
        console.error('Invalid API response:', response);
      }
    } catch (error) {
      console.error('Error adding evaluation:', error);

      // Enhanced error handling
      if (error && typeof error === 'object') {
        if ('exception' in error) {
          const errorMsg = error.exception as string;
          if (errorMsg.includes('ValidationError')) {
            toast.error('Validation Error: Please check your evaluation data');
          } else if (errorMsg.includes('PermissionError')) {
            toast.error('Permission Error: You may not have access to add evaluations');
          } else {
            toast.error('Server Error: Please try again or contact support');
          }
        } else if ('message' in error) {
          toast.error(`Error: ${error.message}`);
        } else {
          toast.error('Failed to add evaluation: Unknown error');
        }
      } else {
        toast.error('Failed to add evaluation: Network or connection error');
      }
    }
  };



  // Comment type categorization functions
  const getCommentType = (comment: Comment) => {
    // If comment has explicit type, use it
    if (comment.comment_type) {
      switch (comment.comment_type) {
        case 'critical': return 'critical';
        case 'positive': return 'positive';
        case 'warning': case 'attention': return 'attention';
        case 'teaching': case 'general': return 'teaching';
        default: break;
      }
    }

    // Fall back to text analysis
    const text = comment.comment_text.toLowerCase();

    // Check for critical/negative indicators first (highest priority)
    if (text.includes('❌') || text.includes('critical issue') || text.includes('critical') ||
      text.includes('error') || text.includes('wrong') || text.includes('mistake') ||
      text.includes('danger') || text.includes('unsafe') || text.includes('risk') ||
      text.includes('immediately') || text.includes('stop') || text.includes('incorrect')) {
      return 'critical';
    }

    // Check for positive indicators
    if (text.includes('👍') || text.includes('good') || text.includes('excellent') ||
      text.includes('perfect') || text.includes('well done') || text.includes('correct') ||
      text.includes('great') || text.includes('nice') || text.includes('proper')) {
      return 'positive';
    }

    // Check for warning/attention indicators
    if (text.includes('⚠️') || text.includes('attention') || text.includes('careful') ||
      text.includes('improve') || text.includes('consider') || text.includes('watch') ||
      text.includes('note') || text.includes('adjust') || text.includes('modify')) {
      return 'attention';
    }

    // Check for teaching indicators
    if (text.includes('🎯') || text.includes('learning') || text.includes('technique') ||
      text.includes('demonstrates') || text.includes('example') || text.includes('shows')) {
      return 'teaching';
    }

    // Default to positive for general comments
    return 'positive';
  };

  // Get badge config for comment type - WCAG AA compliant colors
  const getCommentBadge = (type: string) => {
    switch (type) {
      case 'positive':
        return {
          label: '',
          classes: 'bg-green-100 text-green-800',
          dotClasses: 'bg-green-600'
        };
      case 'attention':
        return {
          label: 'Attention',
          classes: 'bg-yellow-100 text-yellow-900', // Improved contrast: 6.2:1
          dotClasses: 'bg-yellow-600'
        };
      case 'critical':
        return {
          label: 'Critical',
          classes: 'bg-red-100 text-red-800',
          dotClasses: 'bg-red-600'
        };
      case 'teaching':
        return {
          label: 'Teaching',
          classes: 'bg-blue-100 text-blue-800',
          dotClasses: 'bg-blue-600'
        };
      default:
        return {
          label: 'General',
          classes: 'bg-gray-100 text-gray-800',
          dotClasses: 'bg-gray-600'
        };
    }
  };

  // Toggle comment expansion
  const toggleCommentExpansion = (commentId: string) => {
    setExpandedComments(prev => {
      const newSet = new Set(prev);
      if (newSet.has(commentId)) {
        newSet.delete(commentId);
      } else {
        newSet.add(commentId);
      }
      return newSet;
    });
  };



  // Return ALL session comments for unified timeline (all videos share same timeline)
  const getVideoComments = (_videoTitle: string) => {
    // Return all comments in the session, regardless of which video they were originally added to
    // This creates a unified timeline where all videos show the same comments/labels
    return comments.sort((a, b) => a.timestamp - b.timestamp);
  };

  const changeLayout = (newLayout: 'single' | 'side-by-side') => {
    setLayout(newLayout);

    // Initialize active videos when switching to side-by-side
    if (newLayout === 'side-by-side' && sessionData) {
      if (activeVideos.length === 0) {
        // Start with first 2 videos by default
        const initialVideos = sessionData.videos.slice(0, 2);
        setActiveVideos(initialVideos);

        // Set timeline video to current video or first video
        if (!timelineVideo) {
          setTimelineVideo(currentVideo || initialVideos[0] || null);
        }

        // Set master video to first video by default
        if (!masterVideo && initialVideos.length > 0) {
          setMasterVideo(initialVideos[0]);
          // Also set timeline video to master video
          setTimelineVideo(initialVideos[0]);
        }
      } else {
        // If activeVideos exist but no master video, set first active video as master
        if (!masterVideo && activeVideos.length > 0) {
          setMasterVideo(activeVideos[0]);
          // Also set timeline video to master video
          setTimelineVideo(activeVideos[0]);
        }
      }
    }
  };

  // Video grid management functions (will be used in next tasks)
  /*
  const addVideoToGrid = (video: Video) => {
    if (activeVideos.length >= maxVideosInGrid) {
      toast.error(`Maximum ${maxVideosInGrid} videos can be displayed at once`);
      return;
    }
    
    if (!activeVideos.find(v => v.title === video.title)) {
      setActiveVideos(prev => [...prev, video]);
      toast.success(`Added ${video.title} to grid`);
    } else {
      toast(`${video.title} is already in the grid`);
    }
  };

  const removeVideoFromGrid = (video: Video) => {
    if (activeVideos.length <= 1) {
      toast.error('At least one video must remain in the grid');
      return;
    }
    
    setActiveVideos(prev => prev.filter(v => v.title !== video.title));
    
    // If removed video was the timeline video, switch to first remaining video
    if (timelineVideo?.title === video.title) {
      const remainingVideos = activeVideos.filter(v => v.title !== video.title);
      setTimelineVideo(remainingVideos[0] || null);
      toast(`Timeline switched to ${remainingVideos[0]?.title || 'none'}`);
    }
    
    toast.success(`Removed ${video.title} from grid`);
  };

  const setVideoAsTimeline = (video: Video) => {
    if (!activeVideos.find(v => v.title === video.title)) {
      toast.error('Video must be in the grid to be used as timeline');
      return;
    }
    
    setTimelineVideo(video);
    toast.success(`Timeline set to ${video.title}`);
  };

  // Calculate grid dimensions based on video count
  const getGridDimensions = (videoCount: number) => {
    if (videoCount <= 1) return { cols: 1, rows: 1 };
    if (videoCount === 2) return { cols: 2, rows: 1 };
    if (videoCount <= 4) return { cols: 2, rows: 2 };
    if (videoCount <= 6) return { cols: 3, rows: 2 };
    return { cols: 3, rows: 3 }; // Max 9 videos
  };
  */

  // Helper function to get the video that should receive comments/labels
  const getTargetVideoForComments = (): Video | null => {
    if (layout === 'side-by-side') {
      return masterVideo; // Use master video only in side-by-side mode
    }
    return currentVideo; // Use current video in single mode
  };

  // Get unified session timeline reference - use first video as reference for all comments
  const getSessionTimelineReference = (): Video | null => {
    return sessionData?.videos?.[0] || null;
  };

  // Sync all videos to the same timeline
  const syncVideosToReference = (referenceVideo: Video) => {
    if (!sessionData) {
      toast.error('No session data available for synchronization');
      return;
    }

    // Get the current time from the reference video
    const referenceTime = videoPlayerStates.get(referenceVideo.title)?.currentTime || 0;

    // Get all videos to sync (use activeVideos for side-by-side, all videos for single)
    let videosToSync: Video[] = [];
    if (layout === 'side-by-side') {
      videosToSync = activeVideos;
    } else {
      toast.error('Sync is only available in Side by Side mode');
      return;
    }

    if (videosToSync.length <= 1) {
      toast.error('Need at least 2 videos to synchronize');
      return;
    }

    // Sync all other videos to the reference time
    let syncedCount = 0;
    videosToSync.forEach((video) => {
      if (video.title !== referenceVideo.title) {
        handleSeek(video.title, referenceTime);
        syncedCount++;
      }
    });

    toast.success(`Synchronized ${syncedCount} video${syncedCount !== 1 ? 's' : ''} to ${referenceVideo.title} at ${formatTime(referenceTime)}`);
  };

  // Function to export evaluations as JSON
  const handleExportEvaluations = async () => {
    if (!sessionName) return;

    setIsExporting(true);
    try {
      // Get CSRF token from window
      const csrfToken = (window as any).csrf_token || '';

      const response = await fetch(
        `/api/method/surgical_training.surgical_training.api.evaluation.get_session_evaluations`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Frappe-CSRF-Token': csrfToken,
          },
          body: JSON.stringify({
            session_name: sessionName
          })
        }
      );

      const result = await response.json();

      if (result && result.message && result.message.message === 'Success') {
        // Create a JSON file and download it
        const exportData = result.message.data;
        const fileName = `${sessionName.replace(/\s+/g, '_')}_evaluations.json`;
        const json = JSON.stringify(exportData, null, 2);
        const blob = new Blob([json], { type: 'application/json' });
        const href = URL.createObjectURL(blob);

        // Create a temporary anchor element to trigger download
        const link = document.createElement('a');
        link.href = href;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();

        // Clean up
        document.body.removeChild(link);
        URL.revokeObjectURL(href);

        toast.success('Evaluations exported successfully');
      } else {
        toast.error('Failed to export evaluations');
        console.error('API Error:', result);
      }
    } catch (error) {
      toast.error('Failed to export evaluations');
      console.error('Error exporting evaluations:', error);
    } finally {
      setIsExporting(false);
    }
  };

  // Function to generate doctor summary report
  // @ts-ignore
  const handleGenerateDoctorReport = async () => {
    if (!sessionName) return;

    setIsExporting(true);
    try {
      const csrfToken = (window as any).csrf_token || '';

      const response = await fetch(
        `/api/method/surgical_training.api.reports.generate_doctor_summary_report`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Frappe-CSRF-Token': csrfToken
          },
          body: JSON.stringify({
            session_name: sessionName
          })
        }
      );

      const result = await response.json();

      console.log('Full API response:', result);

      // Try multiple possible response structures
      let reportContent = null;

      // Structure 1: Frappe wrapped response
      if (result?.message?.data?.doctor_summary) {
        reportContent = result.message.data.doctor_summary;
        console.log('Found report content in result.message.data.doctor_summary');
      }
      // Structure 2: Direct API response with success message
      else if (result?.data?.doctor_summary) {
        reportContent = result.data.doctor_summary;
        console.log('Found report content in result.data.doctor_summary');
      }
      // Structure 3: Simple response structure
      else if (result?.doctor_summary) {
        reportContent = result.doctor_summary;
        console.log('Found report content in result.doctor_summary');
      }
      // Structure 4: Alternative content field
      else if (result?.message?.data?.report_content) {
        reportContent = result.message.data.report_content;
        console.log('Found report content in result.message.data.report_content');
      }
      else if (result?.data?.report_content) {
        reportContent = result.data.report_content;
        console.log('Found report content in result.data.report_content');
      }

      if (reportContent) {
        const fileName = `doctor_summary_${sessionName}_${new Date().toISOString().split('T')[0]}.md`;

        // Download the report as before
        const blob = new Blob([reportContent], { type: 'text/markdown' });
        const href = URL.createObjectURL(blob);

        const link = document.createElement('a');
        link.href = href;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();

        document.body.removeChild(link);
        URL.revokeObjectURL(href);

        // Check if report was saved to Assessment Summary
        if (result?.saved_report) {
          toast.success(
            <div>
              <p>Doctor summary report generated and saved!</p>
              {/* <p className="text-sm mt-1">
                <span className="text-blue-600 cursor-pointer underline" 
                      onClick={() => window.open('/isim/dashboard?tab=sessions', '_blank')}>
                  View in Assessment Summary
                </span>
              </p> */}
            </div>,
            { duration: 6000 }
          );
        } else {
          toast.success('Doctor summary report generated and downloaded!');
        }

        console.log('Report generated successfully:', fileName);
      } else if (result?.message === 'Error') {
        toast.error(`Failed to generate report: ${result.error}`);
        console.error('API Error:', result.error);
      } else {
        toast.error('Failed to generate doctor report - unexpected response format');
        console.error('Unexpected API response structure:', result);

        // Additional debugging for structure analysis
        console.log('Available keys in result:', Object.keys(result || {}));
        if (result?.message) {
          console.log('Available keys in result.message:', Object.keys(result.message || {}));
        }
        if (result?.data) {
          console.log('Available keys in result.data:', Object.keys(result.data || {}));
        }
      }
    } catch (error) {
      toast.error('Failed to generate doctor report');
      console.error('Error generating doctor report:', error);
    } finally {
      setIsExporting(false);
    }
  };

  // Function to generate student feedback report
  // @ts-ignore
  const handleGenerateStudentReport = async (studentEmail?: string) => {
    if (!sessionName) return;

    setIsExporting(true);
    try {
      const csrfToken = (window as any).csrf_token || '';

      const response = await fetch(
        `/api/method/surgical_training.api.reports.generate_student_feedback_report`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Frappe-CSRF-Token': csrfToken
          },
          body: JSON.stringify({
            session_name: sessionName,
            student_email: studentEmail
          })
        }
      );

      const result = await response.json();

      // Check for the report in the correct location
      if (result?.message?.data?.student_feedback || result?.message?.data?.report_content) {
        // Get report content from the correct field
        const reportContent = result.message.data.student_feedback || result.message.data.report_content;
        const studentSuffix = studentEmail ? `_${studentEmail.split('@')[0]}` : '_all_students';
        const fileName = `student_feedback_${sessionName}${studentSuffix}_${new Date().toISOString().split('T')[0]}.md`;

        const blob = new Blob([reportContent], { type: 'text/markdown' });
        const href = URL.createObjectURL(blob);

        const link = document.createElement('a');
        link.href = href;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();

        document.body.removeChild(link);
        URL.revokeObjectURL(href);

        // Check if report was saved to Assessment Summary
        if (result?.message?.saved_report || result?.saved_report) {
          toast.success(
            <div>
              <p>Student feedback report generated and saved!</p>
              {/* <p className="text-sm mt-1">
                <span className="text-blue-600 cursor-pointer underline" 
                      onClick={() => window.open('/isim/dashboard?tab=sessions', '_blank')}>
                  View in Assessment Summary
                </span>
              </p> */}
            </div>,
            { duration: 6000 }
          );
        } else {
          toast.success('Student feedback report generated and downloaded!');
        }

        console.log('Report generated successfully:', fileName);
      } else if (result?.message === 'Success' && result?.student_feedback) {
        // Alternative structure - direct response
        const reportContent = result.student_feedback;
        const studentSuffix = studentEmail ? `_${studentEmail.split('@')[0]}` : '_all_students';
        const fileName = `student_feedback_${sessionName}${studentSuffix}_${new Date().toISOString().split('T')[0]}.md`;

        const blob = new Blob([reportContent], { type: 'text/markdown' });
        const href = URL.createObjectURL(blob);

        const link = document.createElement('a');
        link.href = href;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();

        document.body.removeChild(link);
        URL.revokeObjectURL(href);

        // Check if report was saved to Assessment Summary
        if (result?.saved_report) {
          toast.success(
            <div>
              <p>Student feedback report generated and saved!</p>
              {/* <p className="text-sm mt-1">
                <span className="text-blue-600 cursor-pointer underline" 
                      onClick={() => window.open('/isim/dashboard?tab=sessions', '_blank')}>
                  View in Assessment Summary
                </span>
              </p> */}
            </div>,
            { duration: 6000 }
          );
        } else {
          toast.success('Student feedback report generated and downloaded!');
        }

        console.log('Report generated successfully:', fileName);
      } else {
        toast.error('Failed to generate student report - unexpected response format');
        console.error('Unexpected API response structure:', result);
      }
    } catch (error) {
      toast.error('Failed to generate student report');
      console.error('Error generating student report:', error);
    } finally {
      setIsExporting(false);
    }
  };

  // Video selection handlers
  const handleAddVideoToAdditional = (video: Video) => {
    if (!masterVideo) {
      toast.error('Please set a master video first');
      return;
    }

    if (video.title === masterVideo.title) {
      toast.error('Cannot add master video to additional videos');
      return;
    }

    if (activeVideos.find(v => v.title === video.title)) {
      toast(`${video.title} is already in the video list`);
      return;
    }

    setActiveVideos(prev => [...prev, video]);
    toast.success(`Added ${video.title} to additional videos`);
  };

  const handleRemoveVideoFromAdditional = (video: Video) => {
    if (video.title === masterVideo?.title) {
      toast.error('Cannot remove master video');
      return;
    }

    setActiveVideos(prev => prev.filter(v => v.title !== video.title));
    toast.success(`Removed ${video.title} from additional videos`);
  };

  // ISBAR label definitions
  const isbarLabels: Record<string, string[]> = {
    identification: [
      'Requires direct prompting',
      'Introduces themselves but only after a hint',
      'Introduces themselves but information is incomplete',
      'Introduces themselves and information is complete',
    ],
    situation: [
      'Unable to identify the main problem(s)',
      'Identifies the main problem(s) after extended prompting',
      'Identifies the main problem(s) with fewer prompts needed',
      'Identifies and prioritises the main problem(s) unprompted',
    ],
    background_history: [
      'History is unstructured, or contains non-relevant information',
      'Relevant history but frequent clarification needed',
      'Relevant history with few further questions needed',
      'Comprehensive focused history',
    ],
    background_examination: [
      'Examination/significant observations are omitted or irrelevant',
      'Examination/observations are reported but frequent clarification is needed',
      'Examination/observations are reported with few further questions needed',
      'Comprehensive focused examination/observations',
    ],
    assessment: [
      'No logical assessment given',
      'Assessment is logical only after extended questioning',
      'Assessment is logical after minimal questioning',
      'Comprehensive logical assessment without questioning',
    ],
    recommendation: [
      'No clear recommendation is made',
      'Clear recommendation only after extended questioning',
      'Clear recommendation after minimal questioning',
      'Clear comprehensive recommendation without questioning',
    ],
  };

  // Generate ISBAR text from category and score
  const generateIsbarText = (category: string, score: string): string => {
    if (category === 'general' || score === '') return '';

    const label = isbarLabels[category as keyof typeof isbarLabels]?.[parseInt(score)] || '';

    // Format the category name properly
    let categoryName: string;
    if (category === 'background_history') {
      categoryName = 'Background - History';
    } else if (category === 'background_examination') {
      categoryName = 'Background - Examination';
    } else {
      categoryName = category.charAt(0).toUpperCase() + category.slice(1);
    }

    return `[ISBAR] ${categoryName}: ${score} - ${label}`;
  };

  // Remove existing ISBAR text from comment
  const removeIsbarFromComment = (comment: string): string => {
    // Match [ISBAR] ... at the beginning of the comment (with optional line breaks after)
    return comment.replace(/^\[ISBAR\][^\n]*\n*/, '').trim();
  };

  // Update comment with ISBAR text
  const updateCommentWithIsbar = (videoTitle: string, category: string, score: string) => {
    const videoState = videoPlayerStates.get(videoTitle);
    if (!videoState) return;

    const currentComment = videoState.newComment || '';
    const userComment = removeIsbarFromComment(currentComment);
    const isbarText = generateIsbarText(category, score);

    let newComment = '';
    if (isbarText) {
      newComment = userComment ? `${isbarText}\n\n${userComment}` : isbarText;
    } else {
      newComment = userComment;
    }

    handleCommentChange(videoTitle, newComment);
  };

  // Add handler to submit ISBAR evaluation as a comment
  const handleSubmitIsbarEvaluation = async (videoTitle: string) => {
    if (!annotationCommentType || isbarValue === '') return;
    const isbarLabels: Record<string, string[]> = {
      identification: [
        'Requires direct prompting',
        'Introduces themselves but only after a hint',
        'Introduces themselves but information is incomplete',
        'Introduces themselves and information is complete',
      ],
      situation: [
        'Unable to identify the main problem(s)',
        'Identifies the main problem(s) after extended prompting',
        'Identifies the main problem(s) with fewer prompts needed',
        'Identifies and prioritises the main problem(s) unprompted',
      ],
      background_history: [
        'History is unstructured, or contains non-relevant information',
        'Relevant history but frequent clarification needed',
        'Relevant history with few further questions needed',
        'Comprehensive focused history',
      ],
      background_examination: [
        'Examination/significant observations are omitted or irrelevant',
        'Examination/observations are reported but frequent clarification is needed',
        'Examination/observations are reported with few further questions needed',
        'Comprehensive focused examination/observations',
      ],
      assessment: [
        'No logical assessment given',
        'Assessment is logical only after extended questioning',
        'Assessment is logical after minimal questioning',
        'Comprehensive logical assessment without questioning',
      ],
      recommendation: [
        'No clear recommendation is made',
        'Clear recommendation only after extended questioning',
        'Clear recommendation after minimal questioning',
        'Clear comprehensive recommendation without questioning',
      ],
    };
    const label = isbarLabels[annotationCommentType]?.[parseInt(isbarValue)] || '';

    // Format the category name properly
    let categoryName: string;
    if (annotationCommentType === 'background_history') {
      categoryName = 'Background - History';
    } else if (annotationCommentType === 'background_examination') {
      categoryName = 'Background - Examination';
    } else {
      categoryName = annotationCommentType.charAt(0).toUpperCase() + annotationCommentType.slice(1);
    }

    const isbarText = `[ISBAR] ${categoryName}: ${isbarValue} - ${label}`;

    // Start a label with ISBAR evaluation
    handleStartLabel(videoTitle, isbarText, annotationCommentType);
    setIsbarValue('');
  };


  // Check if access check is still pending
  if (isAccessPending) {
    // console.log('🔧 DEBUG SessionDetailMain: Rendering access pending screen');
    return (
      <div className="flex justify-center items-center h-screen bg-gradient-to-br from-gray-50 via-blue-50/30 to-indigo-50/50">
        <div className="relative">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-indigo-200"></div>
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-indigo-600 border-t-transparent absolute top-0 left-0"></div>
        </div>
      </div>
    );
  }

  // Check if user has access to this session
  if (!hasAccess) {
    // console.log('🔧 DEBUG SessionDetailMain: Rendering no access screen');
    return (
      <div className="flex flex-col items-center justify-center h-screen bg-gradient-to-br from-gray-50 via-red-50/30 to-orange-50/50">
        <div className="text-center">
          <div className="h-24 w-24 rounded-full bg-gradient-to-br from-red-100 to-red-200 flex items-center justify-center mb-6">
            <AlertCircle size={40} className="text-red-600" />
          </div>
          <h3 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h3>
          <p className="text-gray-600 mb-6 max-w-md">
            You don't have permission to access this training session. Please contact your administrator to request access.
          </p>
          <Button asChild className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 px-8 py-3 rounded-xl">
            <Link to="/dashboard" className="flex items-center gap-2">
              <ArrowLeft size={16} />
              Back to Dashboard
            </Link>
          </Button>
        </div>
      </div>
    );
  }

  if (isSessionLoading && !sessionData) {
    // console.log('🔧 DEBUG SessionDetailMain: Rendering loading screen - isSessionLoading:', isSessionLoading, 'sessionData:', !!sessionData);
    return (
      <div className="flex justify-center items-center h-screen bg-gradient-to-br from-gray-50 via-blue-50/30 to-indigo-50/50">
        <div className="text-center space-y-4">
          <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center animate-pulse mx-auto">
            <VideoIcon className="w-8 h-8 text-white" />
          </div>
          <div className="space-y-2">
            <h2 className="text-2xl font-bold text-gray-900">Loading Session...</h2>
            <p className="text-gray-600">Please wait while we load your session data.</p>
          </div>
        </div>
      </div>
    );
  }

  // Show error state if there's an error
  if (error) {
    // console.log('🔧 DEBUG SessionDetailMain: Rendering error state - error:', error);
    return (
      <div className="flex flex-col items-center justify-center h-screen bg-gradient-to-br from-gray-50 via-blue-50/30 to-indigo-50/50">
        <div className="text-center space-y-4 max-w-lg">
          <AlertCircle className="h-16 w-16 text-red-500 mx-auto" />
          <h2 className="text-2xl font-bold text-gray-900">Error Loading Session</h2>
          <p className="text-gray-600">{error}</p>



          <div className="flex gap-4 justify-center">
            <Link to="/">
              <Button variant="outline">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Home
              </Button>
            </Link>
            <Button onClick={() => {
              refreshSession();
            }}>
              Try Again
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (!sessionData || !currentVideo) {
    // console.log('🔧 DEBUG SessionDetailMain: Rendering session not found - sessionData:', !!sessionData, 'currentVideo:', !!currentVideo, 'sessionData.videos:', sessionData?.videos?.length);
    if (error) {
      // console.log('🔧 DEBUG SessionDetailMain: Error present:', error);
    }
    return (
      <div className="flex flex-col items-center justify-center h-screen bg-gradient-to-br from-gray-50 via-blue-50/30 to-indigo-50/50">
        <div className="text-center">
          <div className="h-24 w-24 rounded-full bg-gradient-to-br from-red-100 to-red-200 flex items-center justify-center mb-6">
            <VideoIcon size={40} className="text-red-600" />
          </div>
          <h3 className="text-2xl font-bold text-gray-900 mb-2">Session Not Found</h3>
          <p className="text-gray-600 mb-6 max-w-md">
            The requested session could not be found or no videos are available for viewing.
          </p>
          <Button asChild className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 px-8 py-3 rounded-xl">
            <Link to="/dashboard" className="flex items-center gap-2">
              <ArrowLeft size={16} />
              Back to Dashboard
            </Link>
          </Button>
        </div>
      </div>
    );
  }



  // Render a single video with comments
  const renderVideoPlayer = (video: Video) => {
    if (!video) return null;

    const videoState = videoPlayerStates.get(video.title);
    if (!videoState) return null;

    // Debug: video data
    // console.log('renderVideoPlayer - video data:', video);

    const videoComments = getVideoComments(video.title);

    return (
      <div className="bg-white border border-gray-200 shadow-lg rounded-lg overflow-hidden relative">
        {/* Header */}
        <div className="p-4 border-b border-gray-200">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <h2 className="text-xl font-semibold text-gray-900">{video.title}</h2>
              <p className="text-gray-600 mt-1">{video.description}</p>
            </div>

            <div className="flex items-center gap-2 ml-4">
              {/* Layout Toggle Buttons */}
              <div className="flex items-center gap-2 bg-white/50 backdrop-blur-sm p-1 rounded-xl border border-gray-200 shadow-sm">
                <Button
                  variant={layout === 'single' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => changeLayout('single')}
                  className={`group flex items-center gap-1.5 transition-all duration-300 rounded-lg px-2.5 py-1.5 ${layout === 'single'
                    ? 'bg-blue-600 text-white shadow-sm'
                    : 'text-gray-600 hover:bg-gray-100'
                    }`}
                >
                  <Maximize2 size={12} />
                  <span className="text-xs font-medium">Single</span>
                </Button>
                <Button
                  variant={layout === 'side-by-side' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => changeLayout('side-by-side')}
                  className={`group flex items-center gap-1.5 transition-all duration-300 rounded-lg px-2.5 py-1.5 ${layout === 'side-by-side'
                    ? 'bg-blue-600 text-white shadow-sm'
                    : 'text-gray-600 hover:bg-gray-100'
                    }`}
                >
                  <Columns size={12} />
                  <span className="text-xs font-medium">Side by Side</span>
                </Button>
              </div>

              {/* Sync Button - Only show in multi-video modes */}
              {layout === 'side-by-side' && sessionData && sessionData.videos.length >= 2 && (
                <Button
                  onClick={() => syncVideosToReference(video)}
                  variant="outline"
                  size="sm"
                  className="group flex items-center gap-2 transition-all duration-300 transform hover:scale-105 rounded-lg bg-gradient-to-r from-orange-50 to-amber-50 border-orange-200 text-orange-700 hover:from-orange-100 hover:to-amber-100 hover:border-orange-300 shadow-md hover:shadow-lg px-3 py-1.5"
                  title={`Sync all other videos to this video's timeline`}
                >
                  <Zap size={14} />
                  <span className="text-xs font-medium">Sync</span>
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Main Content - Responsive 2-Column Layout */}
        <div className="flex flex-col lg:flex-row min-h-[calc(100vh-200px)]">
          {/* Left Column - Video Player (~70%) */}
          <div className="flex-none lg:flex-1 lg:w-[70%] bg-gray-50 flex flex-col">
            {/* Video Player Container */}
            <div className="flex-1 p-4">
              <div className="relative h-full min-h-[400px] lg:min-h-[60vh] video-player-container">
                {(() => {
                  const videoSrc = video.video_file && video.video_file.trim()
                    ? video.video_file  // Use the path as-is from the backend
                    : '/files/placeholder.mp4';  // fallback for missing video

                  // console.log(`🎥 FRONTEND VIDEO PLAYER RENDER:`, {
                  //   title: video.title,
                  //   raw_video_file: video.video_file,
                  //   computed_src: videoSrc,
                  //   is_current_video: currentVideo?.title === video.title
                  // });

                  return (
                    <CustomVideoPlayer
                      src={videoSrc}
                      title={video.title}
                      comments={videoComments.map(comment => {
                        const customColor = getCommentDisplayColor(comment);
                        return {
                          ...comment,
                          isEvaluation: isEvaluationComment(comment),
                          comment_color: customColor // Ensure custom colors are included
                        };
                      })}
                      isPlaying={videoState.isPlaying}
                      currentTime={videoState.currentTime}
                      onTimeUpdate={(time) => handleProgress(video.title, { playedSeconds: time })}
                      onPlayPause={(playing) => handlePlayPause(video.title, playing)}
                      onSeek={(time) => handleSeek(video.title, time)}
                    />
                  );
                })()}
              </div>
            </div>

            {/* Active Labels Indicator */}
            {getActiveLabelsForVideo(video.title).length > 0 && (
              <div className="mx-4 mb-4 bg-gradient-to-r from-yellow-50 to-orange-50 border border-yellow-200 rounded-lg p-3 shadow-sm">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <div className="h-6 w-6 rounded-full bg-yellow-500 flex items-center justify-center">
                      <Target size={12} className="text-white" />
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900">Active Labels</h3>
                      <p className="text-xs text-gray-600">Labels waiting for end time</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <div className="w-2 h-2 bg-yellow-500 rounded-full animate-pulse"></div>
                    <span className="text-xs font-medium text-gray-700">
                      {getActiveLabelsForVideo(video.title).length} active
                    </span>
                  </div>
                </div>
                <div className="space-y-1">
                  {getActiveLabelsForVideo(video.title).map((label) => (
                    <div key={label.id} className="flex items-center justify-between bg-white p-2 rounded border border-yellow-200">
                      <div className="flex-1">
                        <div className="text-xs font-medium text-gray-900 truncate">{label.comment}</div>
                        <div className="text-xs text-gray-500 flex items-center gap-1">
                          <Clock size={10} />
                          Started at {formatTime(label.startTime)} •
                          Duration: {formatTime(videoState.currentTime - label.startTime)}
                        </div>
                      </div>
                      <div className="flex items-center gap-1 ml-2">
                        <Button
                          onClick={() => handleEndLabel(label.id)}
                          variant="outline"
                          size="sm"
                          className="text-green-700 border-green-200 hover:bg-green-50 px-2 py-1 text-xs h-auto"
                        >
                          End
                        </Button>
                        <Button
                          onClick={() => handleCancelLabel(label.id)}
                          variant="outline"
                          size="sm"
                          className="text-red-700 border-red-200 hover:bg-red-50 px-2 py-1 text-xs h-auto"
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

          </div>

          {/* Right Column - Comments Panel (~30%) */}
          <div className="comments-panel flex flex-col lg:w-[30%] bg-white border-l-0 lg:border-l border-t lg:border-t-0 border-gray-200 min-h-[120vh] max-h-screen">
            {/* Skip Link for Keyboard Users */}
            <a href="#comments-section" className="skip-link sr-only focus:not-sr-only">
              Skip to comments
            </a>

            {/* Comments Header */}
            <div id="comments-section" className="p-4 border-b border-gray-200 bg-gray-50 flex-shrink-0">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <MessageSquare size={18} className="text-blue-500" />
                  Comments
                  <span className="bg-blue-100 text-blue-600 px-2 py-1 rounded-full text-sm font-medium">
                    {videoComments.length}
                    {videoComments.length > 3 && (
                      <span className="ml-1 text-xs opacity-75">• Scroll for more</span>
                    )}
                  </span>
                </h3>
                <div className="flex items-center gap-2">
                  <div className="bg-white px-3 py-1 rounded-full border border-gray-200">
                    <span className="text-sm font-mono text-gray-700">
                      {formatTime(videoState.currentTime)}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Comment Input Section - Refactored */}
            <div className="comments-input-section flex-none bg-white border-b border-gray-200 z-10 shadow-sm">

              {/* Toolbar: ISBAR, Colors */}
              <div className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 bg-gray-50/80 border-b border-gray-100 backdrop-blur-sm transition-all duration-300">

                {/* Left: Type & Colors */}
                <div className="flex items-center gap-3">
                  {/* ISBAR Selector */}
                  <div className="relative group">
                    <select
                      value={annotationCommentType}
                      onChange={(e) => {
                        const val = e.target.value as any;
                        setAnnotationCommentType(val);
                        if (val === 'general') updateCommentWithIsbar(video.title, val, '');
                        else setIsbarValue('');
                      }}
                      className="appearance-none bg-white border border-gray-200 text-gray-700 text-xs rounded-lg pl-2 pr-6 py-1.5 shadow-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 cursor-pointer hover:border-blue-300 transition-all font-medium"
                      disabled={getActiveLabelsForVideo(video.title).length === 0}
                    >
                      <option value="general">General Comment</option>
                      <option value="identification">Identification (I)</option>
                      <option value="situation">Situation (S)</option>
                      <option value="background_history">Background: History (B)</option>
                      <option value="background_examination">Background: Exam (B)</option>
                      <option value="assessment">Assessment (A)</option>
                      <option value="recommendation">Recommendation (R)</option>
                    </select>
                    <ChevronDown size={12} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none group-hover:text-blue-500 transition-colors" />
                  </div>

                  {/* Color Picker (Compact) */}
                  <div className="flex items-center gap-1.5 pl-2 border-l border-gray-200">
                    {commentColorOptions.map(option => (
                      <button
                        key={option.value}
                        onClick={() => setSelectedCommentColor(option.value)}
                        className={`w-3.5 h-3.5 rounded-full transition-all duration-200 ${selectedCommentColor === option.value ? 'ring-2 ring-offset-1 ring-gray-400 scale-110 shadow-sm' : 'hover:scale-125 opacity-70 hover:opacity-100 hover:shadow-sm'}`}
                        style={{ backgroundColor: option.value }}
                        title={option.name}
                        disabled={getActiveLabelsForVideo(video.title).length === 0}
                      />
                    ))}
                    <button
                      onClick={() => setShowColorGuide(true)}
                      className="ml-1 text-gray-400 hover:text-blue-600 transition-colors"
                      title="Color Guide"
                    >
                      <Info size={12} />
                    </button>
                  </div>
                </div>
              </div>

              {/* Conditional ISBAR Score Row */}
              {['identification', 'situation', 'background_history', 'background_examination', 'assessment', 'recommendation'].includes(annotationCommentType) && (
                <div className="px-3 py-2 bg-blue-50/40 border-b border-blue-50 animate-in fade-in slide-in-from-top-1">
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] font-semibold text-blue-700 uppercase tracking-wider">Score</span>
                    <select
                      value={isbarValue}
                      onChange={(e) => {
                        setIsbarValue(e.target.value);
                        updateCommentWithIsbar(video.title, annotationCommentType, e.target.value);
                      }}
                      className="flex-1 bg-white border border-blue-100 text-blue-900 text-xs rounded-md px-2 py-1 focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 shadow-sm"
                    >
                      <option value="">Select relevant score...</option>
                      {annotationCommentType === 'identification' && (
                        <>
                          <option value="0">0 - Requires direct prompting</option>
                          <option value="1">1 - After a hint</option>
                          <option value="2">2 - Information incomplete</option>
                          <option value="3">3 - Information complete</option>
                        </>
                      )}
                      {annotationCommentType === 'situation' && (
                        <>
                          <option value="0">0 - Unable to identify</option>
                          <option value="1">1 - After extended prompting</option>
                          <option value="2">2 - Fewer prompts needed</option>
                          <option value="3">3 - Unprompted</option>
                        </>
                      )}
                      {/* Add other options logic if needed, simplifed for now based on context availability or keep standard list */}
                      {annotationCommentType === 'background_history' && (
                        <>
                          <option value="0">0 - Unstructured/irrelevant</option>
                          <option value="1">1 - Frequent clarification</option>
                          <option value="2">2 - Few questions needed</option>
                          <option value="3">3 - Comprehensive</option>
                        </>
                      )}
                      {annotationCommentType === 'background_examination' && (
                        <>
                          <option value="0">0 - Omitted/irrelevant</option>
                          <option value="1">1 - Clarification needed</option>
                          <option value="2">2 - Few questions needed</option>
                          <option value="3">3 - Comprehensive</option>
                        </>
                      )}
                      {annotationCommentType === 'assessment' && (
                        <>
                          <option value="0">0 - No logical assessment</option>
                          <option value="1">1 - Extended questioning</option>
                          <option value="2">2 - Minimal questioning</option>
                          <option value="3">3 - Independent</option>
                        </>
                      )}
                      {annotationCommentType === 'recommendation' && (
                        <>
                          <option value="0">0 - No clear recommendation</option>
                          <option value="1">1 - Extended questioning</option>
                          <option value="2">2 - Minimal questioning</option>
                          <option value="3">3 - Independent</option>
                        </>
                      )}
                    </select>
                  </div>
                </div>
              )}

              {/* Input Area */}
              <div className="p-3 relative group">
                <textarea
                  id={`comment-input-${video.title}`}
                  rows={3}
                  className="w-full bg-white border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-900 placeholder-gray-400/80 shadow-sm focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 transition-all resize-none disabled:bg-gray-50 disabled:text-gray-400"
                  placeholder={
                    getActiveLabelsForVideo(video.title).length > 0
                      ? "Type your observation here (Ctrl+Enter to save point)..."
                      : "Click 'Start Label' below to begin annotating..."
                  }
                  value={videoPlayerStates.get(video.title)?.newComment || ''}
                  onChange={(e) => handleCommentChange(video.title, e.target.value)}
                  disabled={getActiveLabelsForVideo(video.title).length === 0}
                  onKeyDown={(e) => {
                    if (e.ctrlKey && e.key === 'Enter') {
                      // Quick save point
                      // If active label exists, maybe just let it run? 
                      // Or add a point comment?
                      // For now just prevent default to avoid newline if redundant
                    }
                  }}
                />

                {/* Inline Actions (Voice & Count) */}
                <div className="absolute bottom-5 right-5 flex items-center gap-3 bg-white/50 backdrop-blur-[2px] rounded-lg p-1">
                  <div className="text-[10px] text-gray-400 font-mono select-none">
                    {(videoPlayerStates.get(video.title)?.newComment || '').length}/500
                  </div>
                  <div className="h-3 w-px bg-gray-200"></div>
                  <button
                    onClick={toggleVoiceRecording}
                    className={`transition-all duration-300 rounded-full p-1.5 ${isListening
                        ? 'bg-red-50 text-red-500 animate-pulse ring-2 ring-red-100'
                        : 'text-gray-400 hover:text-blue-600 hover:bg-blue-50'
                      }`}
                    title={isListening ? "Stop Recording" : "Start Voice Input"}
                    disabled={!isVoiceSupported || getActiveLabelsForVideo(video.title).length === 0}
                  >
                    <Mic size={14} />
                  </button>
                </div>
              </div>

              {/* Action Bar */}
              <div className="px-3 pb-3 flex items-center gap-2">
                {getActiveLabelsForVideo(video.title).length === 0 ? (
                  <Button
                    onClick={() => handleStartLabel(video.title, '', annotationCommentType || 'general')}
                    className="flex-1 bg-green-600 hover:bg-green-700 text-white shadow-sm hover:shadow-md transition-all h-10 group"
                  >
                    <div className="p-1 bg-white/20 rounded-full mr-2 group-hover:scale-110 transition-transform">
                      <Play size={12} className="fill-current" />
                    </div>
                    Start Label
                  </Button>
                ) : (
                  <Button
                    onClick={() => {
                      const labels = getActiveLabelsForVideo(video.title);
                      if (labels.length > 0) handleEndLabel(labels[labels.length - 1].id);
                    }}
                    className="flex-1 bg-red-600 hover:bg-red-700 text-white shadow-sm hover:shadow-md transition-all animate-pulse h-10 group"
                  >
                    <div className="p-1 bg-white/20 rounded-full mr-2 group-hover:scale-110 transition-transform">
                      <Square size={12} className="fill-current" />
                    </div>
                    Stop Label ({getActiveLabelsForVideo(video.title).length})
                  </Button>
                )}

                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    handleCommentChange(video.title, '');
                    setIsbarValue('');
                    clearSuggestion();
                    getActiveLabelsForVideo(video.title).forEach(l => handleCancelLabel(l.id));
                  }}
                  className="aspect-square p-0 h-10 w-10 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors border border-gray-200"
                  title="Clear & Cancel"
                  disabled={getActiveLabelsForVideo(video.title).length === 0 && !videoPlayerStates.get(video.title)?.newComment}
                >
                  <Trash2 size={16} />
                </Button>
              </div>
            </div>

            {/* Comments List - Flexible Height with Scrolling */}
            <div
              className="comments-list-section comment-list-container flex-1 overflow-y-auto overflow-x-hidden custom-scrollbar relative"
              role="region"
              aria-label="Video Comments"
              aria-live="polite"
              style={{
                containIntrinsicSize: 'none',
                contain: 'layout style'
              }}
            >
              {/* Scroll indicator gradient - only when content overflows */}
              {videoComments.length > 0 && (
                <>
                  <div
                    className="absolute bottom-0 left-0 right-0 h-4 bg-gradient-to-t from-white/20 to-transparent pointer-events-none z-10"
                    aria-hidden="true"
                    style={{ opacity: videoComments.length > 5 ? 1 : 0 }}
                  />
                  <div className="sr-only" aria-live="polite">
                    {videoComments.length} comments available. Scroll to navigate all comments.
                  </div>
                </>
              )}
              {/* Comments container - flexible height, no fixed constraints */}
              <div className="flex flex-col gap-2 p-2">
                {videoComments.length === 0 ? (
                  <div className="text-center py-12">
                    <MessageSquare size={48} className="mx-auto text-gray-300 mb-4" />
                    <h4 className="text-lg font-medium text-gray-900 mb-2">No Comments Yet</h4>
                    <p className="text-gray-500">Be the first to share your thoughts on this video.</p>
                  </div>
                ) : (
                  videoComments.map((comment, index) => {
                    const commentType = getCommentType(comment);
                    const badge = getCommentBadge(commentType);
                    const isExpanded = expandedComments.has(comment.name);
                    const commentText = comment.comment_text;
                    const shouldShowExpander = commentText.length > 150;

                    // Extract doctor initials for avatar
                    const doctorName = comment.doctor_name || comment.doctor || 'Unknown';
                    const initials = doctorName.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();

                    // Get comment color for badge display
                    const commentColor = getCommentDisplayColor(comment);

                    // Determine if comment has a template label
                    const hasTemplateLabel = badge.label && badge.label !== '';
                    const shouldShowBadge = hasTemplateLabel;

                    // Apply custom color to border for general comments
                    const borderStyle = (!shouldShowBadge && commentColor !== '#FFFFFF') ? {
                      borderColor: commentColor,
                      borderWidth: '2px'
                    } : undefined;

                    return (
                      <article
                        key={`comment-${comment.name}-${index}`}
                        className="group bg-white border rounded-lg p-4 hover:shadow-md transition-all duration-200 flex-shrink-0 min-w-0 w-full"
                        style={borderStyle}
                        role="article"
                        aria-labelledby={`comment-${comment.name}-header`}
                      >
                        {/* Header Row */}
                        <div className="flex items-start gap-3 mb-3">
                          {/* Avatar */}
                          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center flex-shrink-0">
                            <span className="text-white text-sm font-medium">{initials}</span>
                          </div>

                          {/* Content Column */}
                          <div className="flex-1 min-w-0">
                            {/* Top Row: Name, Badge, Actions */}
                            <div className="flex items-center gap-2 mb-1 min-w-0">
                              <div className="flex items-center gap-2 min-w-0 flex-1">
                                <span
                                  id={`comment-${comment.name}-header`}
                                  className="text-sm font-medium text-gray-900 truncate"
                                  aria-label={`Comment by ${doctorName} at ${formatTime(comment.timestamp)}`}
                                >
                                  {doctorName}
                                </span>
                                {/* Status Badge - only show for template comments */}
                                {shouldShowBadge && (
                                  <span
                                    className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium flex-shrink-0 ${badge.classes}`}
                                  >
                                    <span
                                      className={`w-1.5 h-1.5 ${badge.dotClasses} rounded-full mr-1`}
                                    ></span>
                                    {badge.label}
                                  </span>
                                )}
                                {/* Student Tag */}
                                {comment.student_name && comment.student_name.trim() ? (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 border border-green-200 flex-shrink-0">
                                    <GraduationCap size={10} className="mr-1" />
                                    {comment.student_name}
                                  </span>
                                ) : null}
                              </div>

                              {/* Action Icons (Hidden until hover) - Fixed width to prevent overflow */}
                              <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0 ml-auto">
                                {canEditComment(comment) && (
                                  <button
                                    onClick={() => handleEditComment(comment)}
                                    className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-1 rounded transition-colors"
                                    aria-label={`Edit comment by ${doctorName}`}
                                  >
                                    <Edit3 size={14} aria-hidden="true" />
                                  </button>
                                )}
                                {canDeleteComment(comment) && (
                                  <button
                                    onClick={() => setCommentToDelete({ name: comment.name, text: comment.comment_text })}
                                    className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-1 rounded transition-colors"
                                    aria-label={`Delete comment by ${doctorName}`}
                                  >
                                    <Trash2 size={14} aria-hidden="true" />
                                  </button>
                                )}
                              </div>
                            </div>

                            {/* Timestamp & Jump Button */}
                            <div className="flex items-center gap-2 mb-2">
                              <button
                                onClick={() => handleSeek(video.title, comment.timestamp)}
                                className="inline-flex items-center px-2 py-1 bg-blue-50 text-blue-700 rounded-full text-xs font-medium hover:bg-blue-100 transition-colors"
                              >
                                <Clock size={12} className="mr-1" />
                                {formatTime(comment.timestamp)}
                                {comment.duration && ` (+${formatTime(comment.duration)})`}
                              </button>

                              {/* Duration Edit Section */}
                              {comment.duration && editingComment?.name !== comment.name && (
                                <div className="flex items-center gap-1">
                                  {editingDuration === comment.name ? (
                                    <div className="flex items-center gap-1">
                                      <div className="flex items-center gap-0.5">
                                        <input
                                          type="number"
                                          value={editDurationValue}
                                          onChange={(e) => setEditDurationValue(e.target.value)}
                                          onKeyDown={(e) => {
                                            if (e.key === 'Enter') {
                                              handleSaveDuration(comment.name);
                                            } else if (e.key === 'Escape') {
                                              handleCancelDurationEdit();
                                            }
                                          }}
                                          placeholder="30"
                                          className="w-16 px-1 py-0.5 text-xs border border-gray-300 rounded focus:border-blue-500 focus:outline-none"
                                          min="1"
                                          max="600"
                                          autoFocus
                                          title="Duration in seconds (1-600). Press Enter to save, Escape to cancel."
                                        />
                                        <span className="text-xs text-gray-500">s</span>
                                        {editDurationValue && !isNaN(parseInt(editDurationValue)) && (
                                          <span className="text-xs text-blue-600 ml-1">
                                            ({formatTime(parseInt(editDurationValue))})
                                          </span>
                                        )}
                                      </div>
                                      <button
                                        onClick={() => handleSaveDuration(comment.name)}
                                        className="p-0.5 text-green-600 hover:text-green-700 hover:bg-green-50 rounded"
                                        aria-label="Save duration"
                                      >
                                        <Save size={12} />
                                      </button>
                                      <button
                                        onClick={handleCancelDurationEdit}
                                        className="p-0.5 text-gray-500 hover:text-gray-700 hover:bg-gray-50 rounded"
                                        aria-label="Cancel duration edit"
                                      >
                                        <X size={12} />
                                      </button>
                                    </div>
                                  ) : (
                                    <button
                                      onClick={() => handleEditDuration(comment)}
                                      className="p-0.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded"
                                      aria-label="Edit duration"
                                    >
                                      <Edit3 size={12} />
                                    </button>
                                  )}
                                </div>
                              )}

                              <span className="text-xs text-gray-500">{formatDate(comment.created_at)}</span>
                            </div>
                          </div>
                        </div>

                        {/* Comment Text with Expandable Content */}
                        <div className="ml-13 min-w-0"> {/* Offset to align with content above */}
                          {editingComment?.name === comment.name ? (
                            <div className="space-y-2 min-w-0">
                              <textarea
                                value={editCommentText}
                                onChange={(e) => setEditCommentText(e.target.value)}
                                className="w-full p-2 border border-gray-200 rounded-md text-sm resize-none min-w-0"
                                rows={3}
                              />
                              {/* Color Picker for Edit */}
                              <div className="flex items-center gap-2">
                                <span className="text-xs text-gray-600">Color:</span>
                                {commentColorOptions.map(colorOption => (
                                  <button
                                    key={colorOption.value}
                                    onClick={() => setEditCommentColor(colorOption.value)}
                                    className={`w-6 h-6 rounded-full border-2 transition-all ${editCommentColor === colorOption.value ? 'border-gray-800 scale-110' : 'border-gray-300'
                                      }`}
                                    style={{ backgroundColor: colorOption.value }}
                                    title={`${colorOption.name} - ${colorGuide[colorOption.name as keyof typeof colorGuide]}`}
                                  />
                                ))}
                              </div>
                              <div className="flex items-center gap-2 flex-wrap">
                                <Button
                                  onClick={handleSaveComment}
                                  size="sm"
                                  className="bg-green-600 hover:bg-green-700 text-white px-3 py-1 text-xs flex-shrink-0"
                                >
                                  <Save size={12} className="mr-1" />
                                  Save
                                </Button>
                                <Button
                                  onClick={handleCancelEdit}
                                  variant="outline"
                                  size="sm"
                                  className="px-3 py-1 text-xs flex-shrink-0"
                                >
                                  <X size={12} className="mr-1" />
                                  Cancel
                                </Button>
                              </div>
                            </div>
                          ) : (
                            <div className="text-sm text-gray-700 leading-relaxed min-w-0 break-words">
                              {/* Truncated or Full Text */}
                              <div
                                id={`comment-${comment.name}-text`}
                                className={`${shouldShowExpander && !isExpanded ? "line-clamp-3" : ""} break-words word-wrap-break-word`}
                              >
                                {commentText}
                              </div>

                              {/* Expand/Collapse Button */}
                              {shouldShowExpander && (
                                <button
                                  onClick={() => toggleCommentExpansion(comment.name)}
                                  className="text-blue-600 hover:text-blue-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-1 text-xs font-medium mt-1 inline-flex items-center gap-1 rounded px-1"
                                  aria-expanded={isExpanded}
                                  aria-controls={`comment-${comment.name}-text`}
                                  aria-label={isExpanded ? 'Show less of comment' : 'Show more of comment'}
                                >
                                  <span>{isExpanded ? 'Show less' : 'Show more'}</span>
                                  <svg
                                    className={`w-3 h-3 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                    aria-hidden="true"
                                  >
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                                  </svg>
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      </article>
                    );
                  })
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Render AI Recommendations Panel
  const renderAIPanel = () => {
    if (!showAIPanel) return null;

    const getScoreColor = (score: number) => {
      if (score >= 3) return 'text-green-700 bg-green-50 border-green-300';
      if (score >= 2) return 'text-yellow-700 bg-yellow-50 border-yellow-300';
      if (score >= 1) return 'text-orange-700 bg-orange-50 border-orange-300';
      return 'text-red-700 bg-red-50 border-red-300';
    };

    const getScoreLabel = (score: number) => {
      if (score >= 3) return 'Excellent';
      if (score >= 2) return 'Good';
      if (score >= 1) return 'Needs Improvement';
      return 'Poor';
    };

    const isbarComponents = aiRecommendations ? [
      { key: 'identification', label: 'Identification', icon: Users, color: 'blue', bgColor: 'bg-blue-500', textColor: 'text-blue-600' },
      { key: 'situation', label: 'Situation', icon: AlertCircle, color: 'red', bgColor: 'bg-red-500', textColor: 'text-red-600' },
      { key: 'background', label: 'Background', icon: FileText, color: 'purple', bgColor: 'bg-purple-500', textColor: 'text-purple-600' },
      { key: 'assessment', label: 'Assessment', icon: Target, color: 'orange', bgColor: 'bg-orange-500', textColor: 'text-orange-600' },
      { key: 'recommendation', label: 'Recommendation', icon: Award, color: 'green', bgColor: 'bg-green-500', textColor: 'text-green-600' }
    ] : [];

    // Function to handle timestamp click and seek video
    const handleTimestampClick = (timeRange: string) => {
      if (!currentVideo) {
        toast.error('Please select a video first');
        return;
      }

      // Parse time range (e.g., "46.0-60.0" -> 46)
      const startTime = parseFloat(timeRange.split('-')[0]);
      if (!isNaN(startTime)) {
        handleSeek(currentVideo.title, startTime);
        toast.success(`Jumped to ${Math.floor(startTime)}s`);
      }
    };

    return (
      <div className="fixed right-0 top-0 h-full w-96 bg-white shadow-2xl border-l border-gray-200 z-50 overflow-y-auto animate-slide-in-right">
        {/* Header */}
        <div className="sticky top-0 bg-indigo-600 text-white p-4 shadow-md z-10">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <div className="p-2 bg-white/20 rounded-lg backdrop-blur-sm">
                <Brain size={20} />
              </div>
              <h2 className="text-lg font-bold">AI Insights</h2>
            </div>
            <button
              onClick={() => setShowAIPanel(false)}
              className="p-1.5 hover:bg-white/20 rounded-lg transition-colors"
              aria-label="Close AI panel"
            >
              <X size={18} />
            </button>
          </div>

          {aiRecommendations?.global && (
            <div className="flex items-center gap-3 mt-3 bg-indigo-700 rounded-lg p-3">
              <div className="flex items-center gap-2">
                <TrendingUp size={16} />
                <span className="text-sm font-medium">Overall Score:</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-2xl font-bold">{aiRecommendations.global.average.toFixed(1)}</span>
                <span className="text-xs opacity-90">/ 3.0</span>
              </div>
            </div>
          )}
        </div>

        {/* Content */}
        <div className="p-4 space-y-4">
          {isLoadingAI ? (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-4 border-indigo-200 border-t-indigo-600 mb-4"></div>
              <p className="text-gray-600 text-sm">Loading AI insights...</p>
            </div>
          ) : aiRecommendations ? (
            <>
              {isbarComponents.map((component) => {
                const data = aiRecommendations[component.key];
                if (!data || !data.found) return null;

                const Icon = component.icon;

                return (
                  <div key={component.key} className="bg-white border border-gray-200 rounded-lg shadow-sm hover:shadow-md transition-all">
                    {/* Component Header */}
                    <div className="p-3 bg-gray-50 border-b border-gray-200">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <div className={`p-1.5 ${component.bgColor} bg-opacity-10 rounded-lg`}>
                            <Icon size={16} className={component.textColor} />
                          </div>
                          <h3 className="font-semibold text-gray-900">{component.label}</h3>
                        </div>
                        <div className={`px-2 py-1 rounded-full text-xs font-medium border ${getScoreColor(data.score)}`}>
                          {data.score}/3 · {getScoreLabel(data.score)}
                        </div>
                      </div>

                      {/* Time Range - Clickable */}
                      {data.time_range && (
                        <button
                          onClick={() => handleTimestampClick(data.time_range)}
                          className="flex items-center gap-1.5 text-xs text-blue-600 hover:text-blue-800 hover:bg-blue-50 px-2 py-1 rounded transition-colors cursor-pointer group"
                          title="Click to jump to this timestamp"
                        >
                          <Clock size={12} className="group-hover:scale-110 transition-transform" />
                          <span className="font-medium">{data.time_range.replace('-', ' - ')}s</span>
                          <Play size={10} className="opacity-0 group-hover:opacity-100 transition-opacity" />
                        </button>
                      )}
                    </div>

                    {/* Component Body */}
                    <div className="p-3 space-y-3">
                      {/* Summary */}
                      <div className="bg-blue-50 border border-blue-100 rounded-lg p-2.5">
                        <div className="flex items-start gap-2">
                          <Sparkles size={14} className="text-blue-600 mt-0.5 flex-shrink-0" />
                          <p className="text-sm text-gray-700 leading-relaxed">{data.summary}</p>
                        </div>
                      </div>

                      {/* Quotes */}
                      {data.quotes && data.quotes.length > 0 && (
                        <div className="space-y-1.5">
                          <div className="flex items-center gap-1.5 text-xs font-medium text-gray-600">
                            <Quote size={12} />
                            <span>Key Quotes</span>
                          </div>
                          {data.quotes.map((quote: string, idx: number) => (
                            <div key={idx} className="bg-gray-50 border-l-2 border-gray-300 pl-3 py-1.5">
                              <p className="text-xs text-gray-700 italic">"{quote}"</p>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Rationale */}
                      {data.rationale && (
                        <div className="pt-2 border-t border-gray-100">
                          <div className="flex items-start gap-2">
                            <Info size={14} className="text-gray-400 mt-0.5 flex-shrink-0" />
                            <p className="text-xs text-gray-600 leading-relaxed">{data.rationale}</p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </>
          ) : (
            <div className="flex flex-col items-center justify-center py-12 text-center px-4">
              <div className="p-4 bg-gray-100 rounded-full mb-4">
                <Brain size={32} className="text-gray-400" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">No AI Insights Yet</h3>
              <p className="text-sm text-gray-600 mb-4">
                AI analysis will appear here once available for this session.
              </p>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="py-8 px-4 sm:px-6 lg:px-8">
        {/* Enhanced Header Section */}
        <div className="mb-6">
          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-6">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-3">
                <div className="h-12 w-12 rounded-xl bg-blue-600 flex items-center justify-center shadow-lg">
                  <VideoIcon size={24} className="text-white" />
                </div>
                <div className="flex-1">
                  <h1 className="text-3xl font-bold text-gray-900">
                    {sessionData?.session.title}
                  </h1>
                  <div className="flex flex-wrap items-center gap-4 mt-2">
                    <div className="flex items-center gap-2 text-gray-600">
                      <Calendar size={14} />
                      <span className="text-sm">
                        {new Date(sessionData?.session.session_date || '').toLocaleDateString()}
                      </span>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(sessionData?.session.status || '')}`}>
                      {sessionData?.session.status || 'Active'}
                    </span>
                    <div className="flex items-center gap-2 text-gray-600">
                      <Users size={14} />
                      <span className="text-sm">{sessionData?.videos.length || 0} videos</span>
                    </div>

                    {/* Compact Description Preview */}
                    {sessionData?.session.description && (
                      <Button
                        onClick={() => setShowFullDescription(!showFullDescription)}
                        variant="ghost"
                        size="sm"
                        className="text-blue-600 hover:text-blue-700 hover:bg-blue-50 px-2 py-1 h-auto rounded-full transition-all duration-200"
                        title={showFullDescription ? 'Hide session details' :
                          `${sessionData.session.description.replace(/<[^>]*>/g, '').substring(0, 100)}${sessionData.session.description.length > 100 ? '...' : ''}`}
                      >
                        <Info size={12} className="mr-1" />
                        <span className="text-xs font-medium">
                          {showFullDescription ? 'Less' : 'Details'}
                        </span>
                        {showFullDescription ? (
                          <ChevronUp size={12} className="ml-1" />
                        ) : (
                          <ChevronDown size={12} className="ml-1" />
                        )}
                      </Button>
                    )}
                  </div>
                </div>
              </div>

              {/* Collapsible Session Description */}
              {showFullDescription && sessionData?.session.description && (
                <div className="mt-4 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-5 shadow-sm transition-all duration-300 ease-in-out animate-fade-in-up">
                  <div className="flex items-start gap-4">
                    <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center flex-shrink-0 shadow-sm">
                      <FileText size={14} className="text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-base font-semibold text-gray-900 mb-3 flex items-center gap-2">
                        Session Overview
                        <span className="h-1 w-8 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-full"></span>
                      </h3>
                      <div
                        className="prose prose-sm max-w-none text-gray-700 leading-relaxed"
                        dangerouslySetInnerHTML={{
                          __html: sessionData.session.description
                        }}
                      />
                    </div>
                    <Button
                      onClick={() => setShowFullDescription(false)}
                      variant="ghost"
                      size="sm"
                      className="text-gray-400 hover:text-gray-600 hover:bg-white/50 p-2 h-auto rounded-full transition-all duration-200"
                      title="Close description"
                    >
                      ✕
                    </Button>
                  </div>
                </div>
              )}
            </div>

            <div className="flex flex-wrap gap-3">
              <Button
                onClick={() => {
                  // Load AI insights from backend if not already loaded
                  if (!aiRecommendations && !showAIPanel) {
                    setIsLoadingAI(true);
                    setShowAIPanel(true);
                    // Fetch from Frappe API endpoint
                    fetch(`/api/method/surgical_training.api.session.get_ai_isbar_evaluation?session_name=${sessionName}`)
                      .then(res => {
                        if (!res.ok) {
                          throw new Error(`HTTP error! status: ${res.status}`);
                        }
                        return res.json();
                      })
                      .then(result => {
                        console.log('AI insights API response:', result);
                        // Handle Frappe's response format
                        const data = result.message?.data || result.data;
                        if (data) {
                          setAiRecommendations(data);
                          toast.success('AI insights loaded!');
                        } else {
                          throw new Error('No data in response');
                        }
                        setIsLoadingAI(false);
                      })
                      .catch(err => {
                        console.error('Failed to load AI insights:', err);
                        setIsLoadingAI(false);
                        toast.error('Could not load AI insights');
                      });
                  } else {
                    // Just toggle if already loaded
                    setShowAIPanel(!showAIPanel);
                  }
                }}
                variant="outline"
                className={`group ${showAIPanel ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-indigo-50 text-indigo-700 border-indigo-300 hover:bg-indigo-600 hover:text-white hover:border-indigo-600'} transition-all duration-300 transform hover:scale-105 shadow-md hover:shadow-lg`}
              >
                <Brain size={16} className="mr-2 group-hover:scale-110 transition-transform duration-300" />
                {showAIPanel ? 'Hide' : 'Show'} AI Insights
                {aiRecommendations?.global && !showAIPanel && (
                  <span className="ml-2 px-1.5 py-0.5 bg-indigo-600 text-white text-xs rounded-full font-medium">
                    {aiRecommendations.global.average.toFixed(1)}
                  </span>
                )}
              </Button>

              <Button
                onClick={handleExportEvaluations}
                disabled={isExporting}
                variant="outline"
                className="group bg-blue-500/10 hover:bg-blue-500 border-blue-200 text-blue-700 hover:text-white transition-all duration-300 transform hover:scale-105 shadow-md hover:shadow-lg"
              >
                <Download size={16} className="mr-2 group-hover:scale-110 transition-transform duration-300" />
                {isExporting ? 'Exporting...' : 'Export JSON'}
              </Button>

              <Button
                asChild
                variant="outline"
                className="group border-blue-200 text-blue-700 hover:bg-blue-50 transition-all duration-300 transform hover:scale-105 shadow-md hover:shadow-lg"
              >
                <Link to="/dashboard" className="flex items-center gap-2">
                  <ArrowLeft size={16} className="group-hover:-translate-x-1 transition-transform duration-300" />
                  Back to Dashboard
                </Link>
              </Button>
            </div>
          </div>
        </div>

        {/* Video Viewer Section */}
        <div className="mb-8">
          <h2 className="text-2xl md:text-3xl font-semibold text-gray-900 flex items-center gap-3">
            <Eye size={24} className="text-blue-500" />
            Video Viewer
          </h2>
        </div>

        {/* Content based on layout */}
        {layout === 'single' && (
          <div className="w-full">
            {/* Video Player - Full Width */}
            <div>
              {renderVideoPlayer(currentVideo)}
            </div>
          </div>
        )}

        {layout === 'side-by-side' && (
          <div className="w-full">
            {/* Side-by-Side Video Grid */}
            <div className="space-y-6">

              {/* Video Synchronization Controls */}
              <div className="bg-white border border-gray-200 rounded-lg p-3 shadow-sm">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <RefreshCw size={16} className="text-green-500" />
                    <h3 className="text-base font-medium text-gray-900">Video Synchronization</h3>
                  </div>
                  {masterVideo && (
                    <div className="text-xs text-gray-600">
                      Master: <span className="font-medium text-green-600">{masterVideo.title}</span>
                    </div>
                  )}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  {/* Play/Pause Sync Toggle */}
                  <div className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-2">
                      <Play size={16} className="text-blue-500" />
                      <span className="text-sm font-medium text-gray-700">Play/Pause Sync</span>
                    </div>
                    <button
                      onClick={() => setIsPlayPauseSync(!isPlayPauseSync)}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${isPlayPauseSync ? 'bg-blue-600' : 'bg-gray-300'
                        }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${isPlayPauseSync ? 'translate-x-6' : 'translate-x-1'
                          }`}
                      />
                    </button>
                  </div>

                  {/* Seek Sync Toggle */}
                  <div className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-2">
                      <SkipForward size={16} className="text-orange-500" />
                      <span className="text-sm font-medium text-gray-700">Seek Sync</span>
                    </div>
                    <button
                      onClick={() => setIsSeekSync(!isSeekSync)}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${isSeekSync ? 'bg-orange-600' : 'bg-gray-300'
                        }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${isSeekSync ? 'translate-x-6' : 'translate-x-1'
                          }`}
                      />
                    </button>
                  </div>

                  {/* Master Video Selector */}
                  <div className="p-2 bg-gray-50 rounded-lg">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Crown size={16} className="text-yellow-500" />
                        <span className="text-sm font-medium text-gray-700">Master Video:</span>
                      </div>
                      <select
                        value={masterVideo?.title || (activeVideos.length > 0 ? activeVideos[0].title : '')}
                        onChange={(e) => {
                          const selected = activeVideos.find(v => v.title === e.target.value);
                          setMasterVideo(selected || null);
                        }}
                        className="text-xs border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500 max-w-[120px]"
                      >
                        {activeVideos.map((video) => (
                          <option key={video.title} value={video.title}>
                            {video.title}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              </div>

              {/* Master Left, Sub-Videos Right Layout */}
              <div className="space-y-4">
                {/* Video Layout Container */}
                <div className="flex gap-4 h-auto">
                  {/* Master Video - Left Side (2/3 width) */}
                  {masterVideo && (
                    <div className="flex-1 w-2/3">
                      <div className="bg-white border-2 border-yellow-400 rounded-lg overflow-hidden shadow-lg">
                        {/* Master Video Header */}
                        <div className="px-4 py-2 border-b-2 border-yellow-300 bg-yellow-50">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <Crown size={20} className="text-yellow-600" />
                              <div>
                                <h3 className="text-lg font-bold text-gray-900">{masterVideo.title}</h3>
                              </div>
                            </div>
                            <Button
                              onClick={() => syncVideosToReference(masterVideo)}
                              variant="outline"
                              size="sm"
                              className="text-orange-600 border-orange-300 hover:bg-orange-50 font-medium"
                            >
                              <Zap size={16} className="mr-2" />
                              Sync All Videos
                            </Button>
                          </div>
                        </div>

                        {/* Master Video Player */}
                        <div className="p-4" ref={masterVideoRef}>
                          {(() => {
                            const videoState = videoPlayerStates.get(masterVideo.title);
                            if (!videoState) return null;

                            const videoSrc = masterVideo.video_file;
                            const videoComments = getVideoComments(masterVideo.title);

                            return (
                              <CustomVideoPlayer
                                key={`master-${masterVideo.title}`}
                                src={videoSrc}
                                title={masterVideo.title}
                                comments={videoComments.map(comment => ({
                                  ...comment,
                                  isEvaluation: isEvaluationComment(comment),
                                  comment_color: getCommentDisplayColor(comment) // Ensure custom colors are included
                                }))}
                                isPlaying={videoState.isPlaying}
                                currentTime={videoState.currentTime}
                                onTimeUpdate={(time) => handleProgress(masterVideo.title, { playedSeconds: time })}
                                onPlayPause={(playing) => handlePlayPause(masterVideo.title, playing)}
                                onSeek={(time) => handleSeek(masterVideo.title, time)}
                              />
                            );
                          })()}
                        </div>

                        {/* Master Video Labeling Interface */}
                        <div className="bg-white border-2 border-yellow-300 rounded-lg p-4 shadow-md mt-4">
                          <div className="space-y-4">
                            {/* Labeling Header */}
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <Target size={20} className="text-yellow-600" />
                                <div>
                                  <h3 className="text-lg font-bold text-gray-900">Add Labels & Comments</h3>
                                </div>
                              </div>
                              <div className="flex items-center gap-4">
                                <div className="text-sm font-mono text-gray-700">
                                  {(() => {
                                    const masterState = videoPlayerStates.get(masterVideo.title);
                                    const currentTime = masterState?.currentTime || 0;
                                    const minutes = Math.floor(currentTime / 60);
                                    const seconds = Math.floor(currentTime % 60);
                                    return `${minutes}:${String(seconds).padStart(2, '0')}`;
                                  })()}
                                </div>
                                <Button
                                  onClick={() => {
                                    const masterState = videoPlayerStates.get(masterVideo.title);
                                    if (masterState) {
                                      handlePlayPause(masterVideo.title, !masterState.isPlaying);
                                    }
                                  }}
                                  variant="outline"
                                  size="sm"
                                  className="text-blue-600 border-blue-300 hover:bg-blue-50 font-medium"
                                >
                                  {(() => {
                                    const masterState = videoPlayerStates.get(masterVideo.title);
                                    return masterState?.isPlaying ? (
                                      <>
                                        <Square size={16} className="mr-2" />
                                        Pause All
                                      </>
                                    ) : (
                                      <>
                                        <Play size={16} className="mr-2" />
                                        Play All
                                      </>
                                    );
                                  })()}
                                </Button>
                              </div>
                            </div>

                            {/* Comment Input */}
                            <div className="space-y-2">
                              <div className="flex items-center gap-2">
                                <MessageSquare size={14} className="text-blue-600" />
                                <label className="text-xs font-medium text-gray-700">
                                  Add Custom Comment
                                </label>
                              </div>
                              <div className="flex gap-2">
                                <textarea
                                  value={masterVideo ? videoPlayerStates.get(masterVideo.title)?.newComment || '' : ''}
                                  onChange={(e) => {
                                    if (masterVideo) {
                                      handleCommentChange(masterVideo.title, e.target.value);
                                    }
                                  }}
                                  placeholder="Describe what you observe..."
                                  className="flex-1 p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none text-xs"
                                  rows={2}
                                />
                                <Button
                                  onClick={() => {
                                    if (masterVideo) {
                                      const comment = videoPlayerStates.get(masterVideo.title)?.newComment.trim();
                                      if (comment) {
                                        handleStartLabel(masterVideo.title, comment, annotationCommentType);
                                      }
                                    }
                                  }}
                                  className="bg-blue-600 hover:bg-blue-700 text-white font-medium px-3 py-1 text-xs"
                                  disabled={!masterVideo || !videoPlayerStates.get(masterVideo.title)?.newComment?.trim()}
                                >
                                  <Send size={14} className="mr-1" />
                                  Add
                                </Button>
                              </div>
                            </div>

                            {/* Active Labels Display */}
                            {masterVideo && getActiveLabelsForVideo(masterVideo.title).length > 0 && (
                              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                                <div className="flex items-center justify-between mb-2">
                                  <div className="flex items-center gap-2">
                                    <div className="h-6 w-6 rounded-full bg-yellow-500 flex items-center justify-center">
                                      <Target size={12} className="text-white" />
                                    </div>
                                    <span className="text-sm font-bold text-gray-900">Active Labels</span>
                                  </div>
                                </div>
                                <div className="space-y-2">
                                  {getActiveLabelsForVideo(masterVideo.title).map((label) => (
                                    <div key={label.id} className="flex items-center justify-between bg-white rounded-md px-3 py-2 border">
                                      <div className="flex items-center gap-2">
                                        <div className="h-4 w-4 rounded-full bg-yellow-400 flex items-center justify-center">
                                          <Target size={8} className="text-white" />
                                        </div>
                                        <div>
                                          <span className="text-sm font-medium text-gray-900">{label.comment}</span>
                                          <div className="text-xs text-gray-500">
                                            Started at {formatTime(label.startTime)}
                                          </div>
                                        </div>
                                      </div>
                                      <div className="flex items-center gap-1">
                                        <Button
                                          onClick={() => handleCancelLabel(label.id)}
                                          size="sm"
                                          className="bg-red-600 hover:bg-red-700 text-white text-xs px-2 py-1 h-6"
                                        >
                                          Cancel
                                        </Button>
                                        <Button
                                          onClick={() => handleEndLabel(label.id)}
                                          size="sm"
                                          className="bg-yellow-600 hover:bg-yellow-700 text-white text-xs px-2 py-1 h-6"
                                        >
                                          Stop Label
                                        </Button>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Sub-Videos - Right Side Column (1/3 width) */}
                  <AdditionalVideosPanel
                    activeVideos={activeVideos}
                    masterVideo={masterVideo}
                    masterVideoHeight={masterVideoHeight}
                    videoPlayerStates={videoPlayerStates}
                    onShowVideoSelection={() => setShowVideoSelection(true)}
                    onHandleProgress={handleProgress}
                    onHandlePlayPause={handlePlayPause}
                    onHandleSeek={handleSeek}
                  />
                </div>
              </div>


              {/* Comment History Section - Moved Below for Space Optimization */}
              {masterVideo && (
                <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
                  <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <MessageSquare size={18} className="text-blue-500" />
                        <h3 className="text-lg font-bold text-gray-900">Comment History</h3>
                        <span className="bg-yellow-100 text-yellow-700 px-2 py-1 rounded-full text-xs font-medium">
                          {masterVideo.title}
                        </span>
                      </div>
                      <div className="text-sm text-gray-600">
                        {getVideoComments(masterVideo.title).length} comments
                      </div>
                    </div>
                  </div>

                  <div className="max-h-96 overflow-y-auto">
                    {getVideoComments(masterVideo.title).length > 0 ? (
                      <div className="divide-y divide-gray-100">
                        {getVideoComments(masterVideo.title).map((comment) => {
                          const commentType = getCommentType(comment);
                          const badge = getCommentBadge(commentType);
                          const isExpanded = expandedComments.has(comment.name);
                          const isLongComment = comment.comment_text.length > 150;

                          // Get comment color for badge display
                          const commentColor = getCommentDisplayColor(comment);

                          // Determine if comment has a template label
                          const hasTemplateLabel = badge.label && badge.label !== '';
                          const shouldShowBadge = hasTemplateLabel;

                          // Apply custom color to border for comments without template labels
                          const borderStyle = (!shouldShowBadge && commentColor !== '#FFFFFF') ? {
                            borderLeftColor: commentColor,
                            borderLeftWidth: '4px'
                          } : undefined;

                          return (
                            <div
                              key={comment.name}
                              className="p-4 bg-white border-l-4 hover:bg-gray-50 transition-colors"
                              style={borderStyle || { borderLeftColor: '#E5E7EB' }}
                            >
                              <div className="flex items-start justify-between gap-3">
                                <div className="flex-1 min-w-0">
                                  {/* Comment Header */}
                                  <div className="flex items-center gap-2 mb-2">
                                    {shouldShowBadge && (
                                      <span
                                        className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${badge.classes}`}
                                      >
                                        <span
                                          className={`w-2 h-2 rounded-full mr-1 ${badge.dotClasses}`}
                                        ></span>
                                        {badge.label}
                                      </span>
                                    )}
                                    {/* Student Tag */}
                                    {comment.student_name && comment.student_name.trim() && (
                                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 border border-green-200">
                                        <GraduationCap size={10} className="mr-1" />
                                        {comment.student_name}
                                      </span>
                                    )}
                                    <span className="text-sm text-gray-500 font-mono">
                                      {formatTime(comment.timestamp)}
                                    </span>
                                    {comment.duration && (
                                      <span className="text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded">
                                        {formatTime(comment.duration)} duration
                                      </span>
                                    )}
                                  </div>

                                  {/* Comment Text */}
                                  <div className="text-sm text-gray-800 leading-relaxed">
                                    {isLongComment && !isExpanded ? (
                                      <>
                                        {comment.comment_text.substring(0, 150)}...
                                        <button
                                          onClick={() => toggleCommentExpansion(comment.name)}
                                          className="ml-2 text-blue-600 hover:text-blue-800 font-medium text-xs"
                                        >
                                          Show more
                                        </button>
                                      </>
                                    ) : (
                                      <>
                                        {comment.comment_text}
                                        {isLongComment && (
                                          <button
                                            onClick={() => toggleCommentExpansion(comment.name)}
                                            className="ml-2 text-blue-600 hover:text-blue-800 font-medium text-xs"
                                          >
                                            Show less
                                          </button>
                                        )}
                                      </>
                                    )}
                                  </div>

                                  {/* Comment Meta */}
                                  <div className="flex items-center gap-3 mt-2 text-xs text-gray-500">
                                    <span>By: {comment.doctor_name || comment.doctor}</span>
                                    <span>•</span>
                                    <span>{new Date(comment.created_at).toLocaleDateString()}</span>
                                  </div>
                                </div>

                                {/* Comment Actions */}
                                <div className="flex items-center gap-1 flex-shrink-0">
                                  <button
                                    onClick={() => handleEditComment(comment)}
                                    className="p-1 text-gray-400 hover:text-blue-600 transition-colors"
                                    title="Edit comment"
                                  >
                                    <Edit3 size={14} />
                                  </button>
                                  <button
                                    onClick={() => handleDeleteComment(comment.name, comment.comment_text)}
                                    className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                                    title="Delete comment"
                                  >
                                    <Trash2 size={14} />
                                  </button>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="p-8 text-center text-gray-500">
                        <MessageSquare size={48} className="mx-auto mb-3 text-gray-300" />
                        <p className="text-sm">No comments yet for {masterVideo.title}</p>
                        <p className="text-xs mt-1">Comments and labels will appear here</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}



        {/* Global Floating Video List Sidebar */}
        <div className="video-list-section">
          {sessionData && (() => {
            // console.log('🎬 FRONTEND VIDEO LIST SIDEBAR DEBUG:', {
            //   total_videos: sessionData.videos.length,
            //   videos: sessionData.videos.map(v => ({ title: v.title, video_file: v.video_file })),
            //   current_video: currentVideo ? { title: currentVideo.title, video_file: currentVideo.video_file } : null
            // });

            return (
              <VideoList
                videos={sessionData.videos}
                currentVideo={currentVideo}
                onVideoChange={handleVideoChange}
                getVideoComments={getVideoComments}
              />
            );
          })()}
        </div>
      </div>

      {/* Floating Comment Modal */}
      {showFloatingComment && currentVideo && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[95vh] border border-gray-200 animate-fade-in-up overflow-hidden flex flex-col">
            {/* Fixed Header */}
            <div className="p-6 border-b border-gray-200 flex-shrink-0">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-blue-600 to-indigo-600 flex items-center justify-center shadow-lg">
                    <MessageSquare size={18} className="text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Add Video Annotation</h3>
                    <p className="text-sm text-gray-600 flex items-center gap-2">
                      <Clock size={12} />
                      At {formatTime(floatingCommentTimestamp)} in {currentVideo.title}
                    </p>
                  </div>
                </div>
                <Button
                  onClick={() => {
                    setShowFloatingComment(false);
                    // Don't reset annotation settings when closing - preserve user preferences
                  }}
                  variant="ghost"
                  size="sm"
                  className="text-gray-400 hover:text-gray-600 p-2 rounded-lg"
                >
                  ✕
                </Button>
              </div>

              {/* Tab Navigation */}
              <div className="mt-4">
                <div className="flex space-x-1 bg-gray-100 p-1 rounded-lg">
                  <button
                    onClick={() => setActiveTab('comment')}
                    className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-md font-medium transition-all duration-200 text-sm ${activeTab === 'comment'
                      ? 'bg-white text-blue-600 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                      }`}
                  >
                    <MessageSquare size={16} />
                    Comment
                  </button>
                  <button
                    onClick={() => setActiveTab('evaluation')}
                    className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-md font-medium transition-all duration-200 text-sm ${activeTab === 'evaluation'
                      ? 'bg-white text-blue-600 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                      }`}
                  >
                    <ClipboardCheck size={16} />
                    Evaluation
                  </button>
                </div>
              </div>

              {/* Compact Timestamp & Settings - Fixed at top */}
              <div className="bg-gray-50 rounded-lg p-3 mt-4">

                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  {/* Timestamp */}
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Start Time
                    </label>
                    <div className="flex items-center gap-2">
                      <input
                        type="number"
                        value={Math.floor(floatingCommentTimestamp)}
                        onChange={(e) => setFloatingCommentTimestamp(parseInt(e.target.value) || 0)}
                        className="w-16 px-2 py-1 border border-gray-200 rounded text-center font-mono text-sm"
                        min="0"
                        max={Math.floor(currentVideo.duration || 0)}
                      />
                      <span className="text-xs text-gray-500">sec</span>
                      <Button
                        onClick={() => setFloatingCommentTimestamp(videoPlayerStates.get(currentVideo.title)?.currentTime || 0)}
                        variant="outline"
                        size="sm"
                        className="text-xs px-2 py-1 h-auto"
                      >
                        Current
                      </Button>
                    </div>
                  </div>



                  {/* ISBAR Category */}
                  <div>
                    <div className="flex items-baseline justify-between mb-1">
                      <label className="block text-xs font-medium text-gray-700">
                        ISBAR Category
                      </label>
                      <Button
                        onClick={() => shouldShowGuide() && setShowISBARGuide(true)}
                        variant="ghost"
                        size="sm"
                        className="h-auto px-1 py-0 text-xs text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                        title="View ISBAR scoring guide"
                      >
                        <Info size={10} className="mr-0.5" />
                        Guide
                      </Button>
                    </div>
                    <select
                      value={annotationCommentType}
                      onChange={(e) => {
                        const newCategory = e.target.value as any;
                        setAnnotationCommentType(newCategory);
                        setIsbarValue(''); // Clear ISBAR value when category changes
                        // Update comment to remove ISBAR text when switching to general
                        if (newCategory === 'general' && currentVideo) {
                          updateCommentWithIsbar(currentVideo.title, newCategory, '');
                        }
                      }}
                      className="w-full px-2 py-2 border border-gray-200 rounded text-xs h-10"
                    >
                      <option value="identification">I - Identification</option>
                      <option value="situation">S - Situation</option>
                      <option value="background_history">B - Background - History</option>
                      <option value="background_examination">B - Background - Examination</option>
                      <option value="assessment">A - Assessment</option>
                      <option value="recommendation">R - Recommendation</option>
                      <option value="general">General</option>
                    </select>
                  </div>

                  {/* ISBAR Score - show when ISBAR category is selected */}
                  {['identification', 'situation', 'background_history', 'background_examination', 'assessment', 'recommendation'].includes(annotationCommentType) && (
                    <div className="mt-2">
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        ISBAR Score
                      </label>
                      <select
                        value={isbarValue}
                        onChange={(e) => {
                          const newScore = e.target.value;
                          setIsbarValue(newScore);
                          // Update comment with ISBAR text
                          if (currentVideo) {
                            updateCommentWithIsbar(currentVideo.title, annotationCommentType, newScore);
                          }
                        }}
                        className="w-full px-2 py-2 border border-gray-200 rounded text-xs h-10"
                      >
                        <option value="">Select score...</option>
                        {annotationCommentType === 'identification' && (
                          <>
                            <option value="0">0 - Requires direct prompting</option>
                            <option value="1">1 - After a hint</option>
                            <option value="2">2 - Information incomplete</option>
                            <option value="3">3 - Information complete</option>
                          </>
                        )}
                        {annotationCommentType === 'situation' && (
                          <>
                            <option value="0">0 - Unable to identify</option>
                            <option value="1">1 - After extended prompting</option>
                            <option value="2">2 - Fewer prompts needed</option>
                            <option value="3">3 - Unprompted</option>
                          </>
                        )}
                        {annotationCommentType === 'background_history' && (
                          <>
                            <option value="0">0 - Unstructured/non-relevant</option>
                            <option value="1">1 - Frequent clarification needed</option>
                            <option value="2">2 - Few questions needed</option>
                            <option value="3">3 - Comprehensive focused</option>
                          </>
                        )}
                        {annotationCommentType === 'background_examination' && (
                          <>
                            <option value="0">0 - Omitted or irrelevant</option>
                            <option value="1">1 - Frequent clarification needed</option>
                            <option value="2">2 - Few questions needed</option>
                            <option value="3">3 - Comprehensive focused</option>
                          </>
                        )}
                        {annotationCommentType === 'assessment' && (
                          <>
                            <option value="0">0 - No logical assessment</option>
                            <option value="1">1 - After extended questioning</option>
                            <option value="2">2 - After minimal questioning</option>
                            <option value="3">3 - Without questioning</option>
                          </>
                        )}
                        {annotationCommentType === 'recommendation' && (
                          <>
                            <option value="0">0 - No clear recommendation</option>
                            <option value="1">1 - After extended questioning</option>
                            <option value="2">2 - After minimal questioning</option>
                            <option value="3">3 - Without questioning</option>
                          </>
                        )}
                      </select>
                    </div>
                  )}
                </div>


              </div>
            </div>

            {/* Scrollable Content Area */}
            <div className="flex-1 overflow-y-auto h-full">
              <div className="p-6 space-y-4">
                {/* Tab Content */}
                {activeTab === 'comment' ? (
                  <>
                    {/* Comment Content */}
                    <div className="space-y-4">
                      <label className="block text-base font-medium text-gray-700">
                        Your Observation
                      </label>
                      <div className="relative">
                        <textarea
                          rows={6}
                          className="w-full rounded-xl border border-gray-200 bg-white shadow-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 px-4 py-3 text-sm text-gray-900 placeholder-gray-500 transition-all duration-300 resize-none"
                          placeholder="Describe what you observe at this moment. Include technical details, technique assessment, or areas for improvement..."
                          value={videoPlayerStates.get(currentVideo.title)?.newComment || ''}
                          onChange={(e) => handleCommentChange(currentVideo.title, e.target.value)}
                          disabled={isAddingComment}
                        />

                        {/* Character Counter */}
                        <div className="absolute bottom-3 right-3 text-xs text-gray-400">
                          {(videoPlayerStates.get(currentVideo.title)?.newComment || '').length}/500
                        </div>
                      </div>

                      {/* Color Picker for Floating Comment */}
                      <div className="mt-4">
                        <div className="flex items-center gap-3">
                          <span className="text-sm font-medium text-gray-700">Comment Color:</span>
                          <div className="flex items-center gap-2">
                            {commentColorOptions.map((color) => (
                              <button
                                key={color.value}
                                onClick={() => setSelectedCommentColor(color.value)}
                                className={`w-6 h-6 rounded-full border-2 transition-all duration-200 hover:scale-110 ${selectedCommentColor === color.value
                                  ? 'border-gray-800 ring-2 ring-gray-300'
                                  : 'border-gray-300 hover:border-gray-500'
                                  }`}
                                style={{ backgroundColor: color.value }}
                                title={`Select ${color.name} color for comment`}
                                aria-label={`Select ${color.name} color for comment`}
                              />
                            ))}
                          </div>
                          <Button
                            onClick={() => setShowColorGuide(true)}
                            variant="ghost"
                            size="sm"
                            className="h-7 px-3 text-xs text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                            title="View color guide for consistent labeling"
                          >
                            <Info size={12} className="mr-1" />
                            Guide
                          </Button>
                          <div
                            className="ml-2 px-3 py-1 rounded-full text-xs font-medium border"
                            style={{
                              backgroundColor: selectedCommentColor,
                              color: selectedCommentColor === '#FFFFFF' ? '#374151' : '#FFFFFF',
                              borderColor: selectedCommentColor
                            }}
                          >
                            Preview
                          </div>
                        </div>

                        {/* Voice Transcript Overlay */}
                        {isListening && voiceTranscript && (
                          <div className="absolute top-3 left-3 right-16 bg-blue-50 border border-blue-200 rounded-lg p-3">
                            <div className="flex items-center gap-2 mb-2">
                              <div className="flex items-center gap-1">
                                <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
                                <span className="text-sm font-medium text-blue-700">🎤 Listening...</span>
                              </div>
                            </div>
                            <div className="text-sm text-blue-800 bg-white/50 rounded-lg px-2 py-1">
                              {voiceTranscript || 'Start speaking...'}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                  </>
                ) : (
                  <>
                    {/* Evaluation Form Tab Content */}
                    <div className="space-y-3">
                      {/* Compact Progress Indicator */}
                      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-3">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Target size={14} className="text-blue-500" />
                            <span className="font-medium text-gray-900 text-sm">Performance Assessment</span>
                          </div>
                          <div className="text-xs text-gray-600">
                            {Object.values(evaluationData).slice(0, 7).filter(v => v !== '').length}/7 completed
                          </div>
                        </div>
                      </div>

                      {/* Evaluation Grid - Compact View */}
                      <div className="grid grid-cols-1 gap-2">
                        {[
                          { key: 'identification', title: 'ID', icon: '👤', desc: 'Introduction & identification' },
                          { key: 'situation', title: 'Situation', icon: '🎯', desc: 'Problem identification' },
                          { key: 'history', title: 'History', icon: '📋', desc: 'Background information' },
                          { key: 'examination', title: 'Examination', icon: '🔍', desc: 'Observations & examination' },
                          { key: 'assessment', title: 'Assessment', icon: '🧠', desc: 'Logical assessment' },
                          { key: 'recommendation', title: 'Recommendation', icon: '💡', desc: 'Clear recommendations' },
                          { key: 'grs', title: 'Global Rating', icon: '⭐', desc: 'Overall confidence level' }
                        ].map((category) => {
                          const currentValue = evaluationData[category.key as keyof EvaluationData];
                          const isExpanded = evaluationExpanded[category.key];

                          return (
                            <div key={category.key} className="border border-gray-200 rounded-lg bg-white">
                              {/* Compact Category Header */}
                              <div className="p-2">
                                <button
                                  onClick={() => toggleEvaluationExpanded(category.key)}
                                  className="w-full flex items-center justify-between hover:bg-gray-50 rounded-md p-1 transition-colors"
                                >
                                  <div className="flex items-center gap-2">
                                    <span className="text-lg">{category.icon}</span>
                                    <div className="text-left">
                                      <div className="text-sm font-medium text-gray-900">{category.title}</div>
                                      <div className="text-xs text-gray-500">{category.desc}</div>
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    {currentValue && (
                                      <span className="text-xs px-2 py-1 bg-green-100 text-green-700 rounded">
                                        {getEvaluationLabel(category.key, currentValue)}
                                      </span>
                                    )}
                                    <ChevronDown size={14} className={`text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''
                                      }`} />
                                  </div>
                                </button>

                                {/* Compact Rating Options */}
                                {isExpanded && (
                                  <div className="mt-2 p-2 bg-gray-50 rounded-md">
                                    <div className="grid grid-cols-2 gap-1">
                                      {['poor', 'fair', 'good', 'excellent'].map((option) => (
                                        <button
                                          key={option}
                                          onClick={() => handleEvaluationChange(category.key as keyof EvaluationData, option)}
                                          className={`p-2 text-xs rounded-md transition-colors ${currentValue === option
                                            ? 'bg-blue-600 text-white'
                                            : 'bg-white text-gray-700 hover:bg-blue-50 border border-gray-200'
                                            }`}
                                        >
                                          {getEvaluationLabel(category.key, option)}
                                        </button>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>

                      {/* Additional Comment Section */}
                      <div className="border border-gray-200 rounded-lg bg-white p-3">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          📝 Additional Comments
                        </label>
                        <textarea
                          rows={3}
                          className="w-full px-3 py-2 border border-gray-200 rounded-md text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 resize-none"
                          placeholder="Any additional observations or comments..."
                          value={evaluationData.comment}
                          onChange={(e) => handleEvaluationChange('comment', e.target.value)}
                        />
                      </div>

                      {/* Reset Button */}
                      <div className="flex justify-start">
                        <Button
                          onClick={resetEvaluationForm}
                          variant="outline"
                          size="sm"
                          className="text-gray-600 border-gray-200 hover:bg-gray-50 px-3 py-2"
                        >
                          <X size={14} className="mr-1" />
                          Reset
                        </Button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Fixed Footer with Actions */}
            <div className="border-t border-gray-200 p-4 bg-gray-50 flex-shrink-0">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {/* Voice Recording */}
                  <div className="relative">
                    <Button
                      onClick={toggleVoiceRecording}
                      variant="outline"
                      size="sm"
                      disabled={!isVoiceSupported}
                      className={`flex items-center gap-2 transition-all duration-300 px-3 py-2 ${isListening
                        ? 'border-red-200 text-red-700 bg-red-50'
                        : microphonePermission === 'denied'
                          ? 'border-gray-300 text-gray-400 cursor-not-allowed'
                          : 'border-blue-200 text-blue-700 hover:bg-blue-50'
                        }`}
                      title={
                        !isVoiceSupported
                          ? 'Speech recognition not supported'
                          : microphonePermission === 'denied'
                            ? 'Microphone access denied'
                            : isListening
                              ? 'Stop recording'
                              : 'Start voice recording'
                      }
                    >
                      <Mic size={14} className={isListening ? 'animate-pulse' : ''} />
                      <span className="text-sm">{isListening ? 'Stop' : 'Voice'}</span>
                    </Button>

                    {isListening && (
                      <div className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full animate-ping"></div>
                    )}
                  </div>

                  {/* Voice controls */}
                  {voiceTranscript && !isListening && (
                    <div className="flex items-center gap-2">
                      <Button
                        onClick={insertVoiceTranscript}
                        variant="outline"
                        size="sm"
                        className="text-green-700 border-green-200 hover:bg-green-50 px-3 py-2"
                      >
                        <CheckCircle size={12} />
                        <span className="text-sm ml-1">Use</span>
                      </Button>
                      <Button
                        onClick={clearVoiceTranscript}
                        variant="outline"
                        size="sm"
                        className="text-gray-600 border-gray-200 hover:bg-gray-50 px-3 py-2"
                      >
                        <span className="text-sm">Clear</span>
                      </Button>
                    </div>
                  )}
                </div>

                <div className="flex items-center gap-3">
                  <Button
                    onClick={() => {
                      setShowFloatingComment(false);
                      // Don't reset annotation settings when canceling - preserve user preferences
                    }}
                    variant="outline"
                    size="sm"
                    className="px-4 py-2"
                  >
                    Cancel
                  </Button>
                  {activeTab === 'comment' ? (
                    <div className="flex items-center gap-2">
                      {true ? (
                        <Button
                          onClick={() => {
                            const comment = videoPlayerStates.get(currentVideo.title)?.newComment.trim();
                            if (comment) {
                              handleStartLabel(currentVideo.title, comment, annotationCommentType);
                              setShowFloatingComment(false);
                            }
                          }}
                          disabled={isAddingComment || !videoPlayerStates.get(currentVideo.title)?.newComment.trim()}
                          size="sm"
                          className="bg-green-600 hover:bg-green-700 text-white px-4 py-2"
                        >
                          <Target size={14} className="mr-1" />
                          {isAddingComment ? 'Starting...' : 'Start Label'}
                        </Button>
                      ) : (
                        <Button
                          onClick={() => {
                            // Update the timestamp first
                            if (currentVideo) {
                              const currentState = videoPlayerStates.get(currentVideo.title);
                              if (currentState) {
                                setVideoPlayerStates(prevStates => {
                                  const newStates = new Map(prevStates);
                                  newStates.set(currentVideo.title, {
                                    ...currentState,
                                    currentTime: floatingCommentTimestamp
                                  });
                                  return newStates;
                                });
                              }
                            }
                            // Add the comment with duration and type
                            currentVideo && handleAddComment(currentVideo.title, floatingCommentTimestamp, annotationDuration, annotationCommentType);
                            setShowFloatingComment(false);
                            // Don't reset annotation settings after successful submission - preserve user preferences
                          }}
                          disabled={isAddingComment || !currentVideo || !videoPlayerStates.get(currentVideo?.title || '')?.newComment.trim()}
                          size="sm"
                          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2"
                        >
                          <Send size={14} className="mr-1" />
                          {isAddingComment ? 'Adding...' : 'Add Annotation'}
                        </Button>
                      )}
                    </div>
                  ) : (
                    <Button
                      onClick={handleAddEvaluation}
                      disabled={isAddingComment || Object.values(evaluationData).slice(0, 7).every(v => !v)}
                      size="sm"
                      className="bg-green-600 hover:bg-green-700 text-white px-4 py-2"
                    >
                      <ClipboardCheck size={14} className="mr-1" />
                      {isAddingComment ? 'Adding...' : 'Add Evaluation'}
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {commentToDelete && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full border border-gray-200 animate-fade-in-up p-6">
            <div className="flex items-center gap-4 mb-6">
              <div className="h-12 w-12 rounded-xl bg-red-100 flex items-center justify-center">
                <AlertCircle size={24} className="text-red-600" />
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-semibold text-gray-900">Delete Comment?</h3>
                <p className="text-sm text-gray-600 mt-1">This action cannot be undone.</p>
              </div>
            </div>

            <div className="bg-gray-50 rounded-xl p-4 mb-6">
              <p className="text-sm text-gray-700 whitespace-pre-wrap break-words">
                {commentToDelete.text}
              </p>
            </div>

            <div className="flex items-center justify-end gap-3">
              <Button
                onClick={() => setCommentToDelete(null)}
                variant="outline"
                className="border-gray-200"
              >
                Cancel
              </Button>
              <Button
                onClick={() => handleDeleteComment(commentToDelete.name, commentToDelete.text)}
                disabled={isDeletingComment}
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                {isDeletingComment ? 'Deleting...' : 'Delete Comment'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Template Manager Modal */}
      <TemplateManagerModal
        isOpen={showTemplateManager}
        onClose={() => setShowTemplateManager(false)}
        customTemplates={customTemplates}
        newTemplate={newTemplate}
        editingTemplate={editingTemplate}
        onNewTemplateChange={(field, value) => setNewTemplate(prev => ({ ...prev, [field]: value }))}
        onCreateTemplate={handleCreateTemplate}
        onEditTemplate={handleEditTemplate}
        onUpdateTemplate={handleUpdateTemplate}
        onCancelTemplateEdit={handleCancelTemplateEdit}
        onDeleteTemplate={handleDeleteTemplate}
        onEditingTemplateChange={(field, value) => setEditingTemplate(prev => prev ? { ...prev, [field]: value } : null)}
      />

      {/* Video Selection Modal */}
      <VideoSelectionModal
        isOpen={showVideoSelection}
        onClose={() => setShowVideoSelection(false)}
        sessionData={sessionData}
        activeVideos={activeVideos}
        masterVideo={masterVideo}
        onAddVideo={handleAddVideoToAdditional}
        onRemoveVideo={handleRemoveVideoFromAdditional}
      />


      {/* Color Guide Modal */}
      {showColorGuide && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] border border-gray-200 animate-fade-in-up overflow-hidden flex flex-col">
            {/* Header */}
            <div className="p-6 border-b border-gray-200 flex-shrink-0">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-blue-600 to-indigo-600 flex items-center justify-center shadow-lg">
                    <Info size={18} className="text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Color Guide</h3>
                    <p className="text-sm text-gray-600">Choose consistent colors for better labeling</p>
                  </div>
                </div>
                <Button
                  onClick={() => setShowColorGuide(false)}
                  variant="ghost"
                  size="sm"
                  className="text-gray-400 hover:text-gray-600 p-2 rounded-lg"
                >
                  ✕
                </Button>
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6">
              <div className="space-y-4">
                <div className="text-sm text-gray-600 mb-6">
                  Use these color recommendations to maintain consistency across all users when labeling comments:
                </div>

                <div className="grid grid-cols-1 gap-4">
                  {commentColorOptions.map((color) => (
                    <div key={color.name} className="flex items-center gap-4 p-4 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors">
                      <div className="flex items-center gap-3">
                        <div
                          className="w-8 h-8 rounded-full border-2 border-gray-300 shadow-sm"
                          style={{ backgroundColor: color.value }}
                        />
                        <div className="flex-1">
                          <div className="font-semibold text-gray-900 text-base">{color.name}</div>
                          <div className="text-sm text-gray-600 mt-1">
                            {colorGuide[color.name as keyof typeof colorGuide]}
                          </div>
                        </div>
                      </div>
                      <Button
                        onClick={() => {
                          setSelectedCommentColor(color.value);
                          setShowColorGuide(false);
                        }}
                        variant="outline"
                        size="sm"
                        className="text-blue-600 border-blue-200 hover:bg-blue-50"
                      >
                        Select
                      </Button>
                    </div>
                  ))}
                </div>

                <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-start gap-3">
                    <Info size={16} className="text-blue-600 mt-0.5" />
                    <div className="text-sm text-blue-800">
                      <div className="font-medium mb-1">Pro Tip:</div>
                      Consistent color usage helps reviewers quickly identify different types of feedback and improves the overall learning experience.
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="border-t border-gray-200 p-4 bg-gray-50 flex-shrink-0">
              <div className="flex justify-end">
                <Button
                  onClick={() => setShowColorGuide(false)}
                  variant="outline"
                  size="sm"
                  className="px-4 py-2"
                >
                  Close
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ISBAR Guide Modal */}
      {showISBARGuide && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] border border-gray-200 animate-fade-in-up overflow-hidden flex flex-col">
            {/* Header */}
            <div className="p-6 border-b border-gray-200 flex-shrink-0">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-blue-600 to-indigo-600 flex items-center justify-center shadow-lg">
                    <Info size={18} className="text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">ISBAR Scoring Guide</h3>
                    <p className="text-sm text-gray-600">Structured communication assessment framework</p>
                  </div>
                </div>
                <Button
                  onClick={() => setShowISBARGuide(false)}
                  variant="ghost"
                  size="sm"
                  className="text-gray-400 hover:text-gray-600 p-2 rounded-lg"
                >
                  ✕
                </Button>
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6">
              <div className="space-y-6">
                <div className="text-sm text-gray-600 mb-6">
                  ISBAR (Identification, Situation, Background, Assessment, Recommendation) is a structured communication framework for clinical handovers. Use these detailed scoring criteria for consistent evaluation:
                </div>

                {/* Header with scoring descriptions */}
                <div className="grid grid-cols-4 gap-2 mb-6 text-xs">
                  <div className="p-3 bg-red-50 border-l-4 border-red-400 rounded">
                    <div className="font-semibold text-red-800">Score 0</div>
                    <div className="text-red-700">Not performed competently</div>
                  </div>
                  <div className="p-3 bg-orange-50 border-l-4 border-orange-400 rounded">
                    <div className="font-semibold text-orange-800">Score 1</div>
                    <div className="text-orange-700">Able to perform under firm direction</div>
                  </div>
                  <div className="p-3 bg-yellow-50 border-l-4 border-yellow-400 rounded">
                    <div className="font-semibold text-yellow-800">Score 2</div>
                    <div className="text-yellow-700">Able to perform under modest direction</div>
                  </div>
                  <div className="p-3 bg-green-50 border-l-4 border-green-400 rounded">
                    <div className="font-semibold text-green-800">Score 3</div>
                    <div className="text-green-700">Able to perform under minimal direction</div>
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-6">
                  {/* Identification */}
                  <div className="border border-gray-200 rounded-lg p-5 bg-gradient-to-r from-blue-50 to-blue-100">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="flex items-center justify-center w-8 h-8 bg-blue-600 text-white rounded-full text-sm font-bold">
                        I
                      </div>
                      <div>
                        <h4 className="text-lg font-semibold text-gray-900">I - Identification</h4>
                        <p className="text-sm text-gray-600">Identification of: 1) self 2) role 3) patient</p>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-start gap-3 p-3 bg-white rounded border-l-4 border-red-400">
                        <span className="flex-shrink-0 w-6 h-6 bg-red-100 text-red-800 rounded-full text-xs font-bold flex items-center justify-center">0</span>
                        <span className="text-sm text-gray-700">Requires direct prompting</span>
                      </div>
                      <div className="flex items-start gap-3 p-3 bg-white rounded border-l-4 border-orange-400">
                        <span className="flex-shrink-0 w-6 h-6 bg-orange-100 text-orange-800 rounded-full text-xs font-bold flex items-center justify-center">1</span>
                        <span className="text-sm text-gray-700">Introduces themselves but only after a hint</span>
                      </div>
                      <div className="flex items-start gap-3 p-3 bg-white rounded border-l-4 border-yellow-400">
                        <span className="flex-shrink-0 w-6 h-6 bg-yellow-100 text-yellow-800 rounded-full text-xs font-bold flex items-center justify-center">2</span>
                        <span className="text-sm text-gray-700">Introduces themselves but information is incomplete</span>
                      </div>
                      <div className="flex items-start gap-3 p-3 bg-white rounded border-l-4 border-green-400">
                        <span className="flex-shrink-0 w-6 h-6 bg-green-100 text-green-800 rounded-full text-xs font-bold flex items-center justify-center">3</span>
                        <span className="text-sm text-gray-700">Introduces themselves and information is complete</span>
                      </div>
                    </div>
                  </div>

                  {/* Situation */}
                  <div className="border border-gray-200 rounded-lg p-5 bg-gradient-to-r from-green-50 to-green-100">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="flex items-center justify-center w-8 h-8 bg-green-600 text-white rounded-full text-sm font-bold">
                        S
                      </div>
                      <div>
                        <h4 className="text-lg font-semibold text-gray-900">S - Situation</h4>
                        <p className="text-sm text-gray-600">Identifies main problem(s)</p>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-start gap-3 p-3 bg-white rounded border-l-4 border-red-400">
                        <span className="flex-shrink-0 w-6 h-6 bg-red-100 text-red-800 rounded-full text-xs font-bold flex items-center justify-center">0</span>
                        <span className="text-sm text-gray-700">Unable to identify the main problem(s)</span>
                      </div>
                      <div className="flex items-start gap-3 p-3 bg-white rounded border-l-4 border-orange-400">
                        <span className="flex-shrink-0 w-6 h-6 bg-orange-100 text-orange-800 rounded-full text-xs font-bold flex items-center justify-center">1</span>
                        <span className="text-sm text-gray-700">Identifies the main problem(s) after extended prompting</span>
                      </div>
                      <div className="flex items-start gap-3 p-3 bg-white rounded border-l-4 border-yellow-400">
                        <span className="flex-shrink-0 w-6 h-6 bg-yellow-100 text-yellow-800 rounded-full text-xs font-bold flex items-center justify-center">2</span>
                        <span className="text-sm text-gray-700">Identifies the main problem(s) with fewer prompts needed</span>
                      </div>
                      <div className="flex items-start gap-3 p-3 bg-white rounded border-l-4 border-green-400">
                        <span className="flex-shrink-0 w-6 h-6 bg-green-100 text-green-800 rounded-full text-xs font-bold flex items-center justify-center">3</span>
                        <span className="text-sm text-gray-700">Identifies and prioritises the main problem(s) unprompted</span>
                      </div>
                    </div>
                  </div>

                  {/* Background */}
                  <div className="border border-gray-200 rounded-lg p-5 bg-gradient-to-r from-purple-50 to-purple-100">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="flex items-center justify-center w-8 h-8 bg-purple-600 text-white rounded-full text-sm font-bold">
                        B
                      </div>
                      <div>
                        <h4 className="text-lg font-semibold text-gray-900">B - Background</h4>
                        <p className="text-sm text-gray-600">History and examination/observations</p>
                      </div>
                    </div>
                    <div className="space-y-4">
                      <div>
                        <div className="text-xs font-medium text-purple-800 mb-2">Gives appropriate history:</div>
                        <div className="space-y-2">
                          <div className="flex items-start gap-3 p-3 bg-white rounded border-l-4 border-red-400">
                            <span className="flex-shrink-0 w-6 h-6 bg-red-100 text-red-800 rounded-full text-xs font-bold flex items-center justify-center">0</span>
                            <span className="text-sm text-gray-700">History is unstructured, or contains non-relevant information</span>
                          </div>
                          <div className="flex items-start gap-3 p-3 bg-white rounded border-l-4 border-orange-400">
                            <span className="flex-shrink-0 w-6 h-6 bg-orange-100 text-orange-800 rounded-full text-xs font-bold flex items-center justify-center">1</span>
                            <span className="text-sm text-gray-700">Relevant history but frequent clarification needed</span>
                          </div>
                          <div className="flex items-start gap-3 p-3 bg-white rounded border-l-4 border-yellow-400">
                            <span className="flex-shrink-0 w-6 h-6 bg-yellow-100 text-yellow-800 rounded-full text-xs font-bold flex items-center justify-center">2</span>
                            <span className="text-sm text-gray-700">Relevant history with few further questions needed</span>
                          </div>
                          <div className="flex items-start gap-3 p-3 bg-white rounded border-l-4 border-green-400">
                            <span className="flex-shrink-0 w-6 h-6 bg-green-100 text-green-800 rounded-full text-xs font-bold flex items-center justify-center">3</span>
                            <span className="text-sm text-gray-700">Comprehensive focused history</span>
                          </div>
                        </div>
                      </div>
                      <div>
                        <div className="text-xs font-medium text-purple-800 mb-2">Gives appropriate examination/observations:</div>
                        <div className="space-y-2">
                          <div className="flex items-start gap-3 p-3 bg-white rounded border-l-4 border-red-400">
                            <span className="flex-shrink-0 w-6 h-6 bg-red-100 text-red-800 rounded-full text-xs font-bold flex items-center justify-center">0</span>
                            <span className="text-sm text-gray-700">Examination/significant observations are omitted or irrelevant</span>
                          </div>
                          <div className="flex items-start gap-3 p-3 bg-white rounded border-l-4 border-orange-400">
                            <span className="flex-shrink-0 w-6 h-6 bg-orange-100 text-orange-800 rounded-full text-xs font-bold flex items-center justify-center">1</span>
                            <span className="text-sm text-gray-700">Examination/observations are reported but frequent clarification is needed</span>
                          </div>
                          <div className="flex items-start gap-3 p-3 bg-white rounded border-l-4 border-yellow-400">
                            <span className="flex-shrink-0 w-6 h-6 bg-yellow-100 text-yellow-800 rounded-full text-xs font-bold flex items-center justify-center">2</span>
                            <span className="text-sm text-gray-700">Examination/observations are reported with few further questions needed</span>
                          </div>
                          <div className="flex items-start gap-3 p-3 bg-white rounded border-l-4 border-green-400">
                            <span className="flex-shrink-0 w-6 h-6 bg-green-100 text-green-800 rounded-full text-xs font-bold flex items-center justify-center">3</span>
                            <span className="text-sm text-gray-700">Comprehensive focused examination/observations</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Assessment */}
                  <div className="border border-gray-200 rounded-lg p-5 bg-gradient-to-r from-yellow-50 to-yellow-100">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="flex items-center justify-center w-8 h-8 bg-yellow-600 text-white rounded-full text-sm font-bold">
                        A
                      </div>
                      <div>
                        <h4 className="text-lg font-semibold text-gray-900">A - Assessment</h4>
                        <p className="text-sm text-gray-600">Makes logical assessment (Correlates problem, history, exam and context)</p>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-start gap-3 p-3 bg-white rounded border-l-4 border-red-400">
                        <span className="flex-shrink-0 w-6 h-6 bg-red-100 text-red-800 rounded-full text-xs font-bold flex items-center justify-center">0</span>
                        <span className="text-sm text-gray-700">No logical assessment given</span>
                      </div>
                      <div className="flex items-start gap-3 p-3 bg-white rounded border-l-4 border-orange-400">
                        <span className="flex-shrink-0 w-6 h-6 bg-orange-100 text-orange-800 rounded-full text-xs font-bold flex items-center justify-center">1</span>
                        <span className="text-sm text-gray-700">Assessment is logical only after extended questioning</span>
                      </div>
                      <div className="flex items-start gap-3 p-3 bg-white rounded border-l-4 border-yellow-400">
                        <span className="flex-shrink-0 w-6 h-6 bg-yellow-100 text-yellow-800 rounded-full text-xs font-bold flex items-center justify-center">2</span>
                        <span className="text-sm text-gray-700">Assessment is logical after minimal questioning</span>
                      </div>
                      <div className="flex items-start gap-3 p-3 bg-white rounded border-l-4 border-green-400">
                        <span className="flex-shrink-0 w-6 h-6 bg-green-100 text-green-800 rounded-full text-xs font-bold flex items-center justify-center">3</span>
                        <span className="text-sm text-gray-700">Comprehensive logical assessment without questioning</span>
                      </div>
                    </div>
                  </div>

                  {/* Recommendation */}
                  <div className="border border-gray-200 rounded-lg p-5 bg-gradient-to-r from-indigo-50 to-indigo-100">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="flex items-center justify-center w-8 h-8 bg-indigo-600 text-white rounded-full text-sm font-bold">
                        R
                      </div>
                      <div>
                        <h4 className="text-lg font-semibold text-gray-900">R - Recommendation</h4>
                        <p className="text-sm text-gray-600">Makes a clear recommendation</p>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-start gap-3 p-3 bg-white rounded border-l-4 border-red-400">
                        <span className="flex-shrink-0 w-6 h-6 bg-red-100 text-red-800 rounded-full text-xs font-bold flex items-center justify-center">0</span>
                        <span className="text-sm text-gray-700">No clear recommendation is made</span>
                      </div>
                      <div className="flex items-start gap-3 p-3 bg-white rounded border-l-4 border-orange-400">
                        <span className="flex-shrink-0 w-6 h-6 bg-orange-100 text-orange-800 rounded-full text-xs font-bold flex items-center justify-center">1</span>
                        <span className="text-sm text-gray-700">Clear recommendation only after extended questioning</span>
                      </div>
                      <div className="flex items-start gap-3 p-3 bg-white rounded border-l-4 border-yellow-400">
                        <span className="flex-shrink-0 w-6 h-6 bg-yellow-100 text-yellow-800 rounded-full text-xs font-bold flex items-center justify-center">2</span>
                        <span className="text-sm text-gray-700">Clear recommendation after minimal questioning</span>
                      </div>
                      <div className="flex items-start gap-3 p-3 bg-white rounded border-l-4 border-green-400">
                        <span className="flex-shrink-0 w-6 h-6 bg-green-100 text-green-800 rounded-full text-xs font-bold flex items-center justify-center">3</span>
                        <span className="text-sm text-gray-700">Clear comprehensive recommendation without questioning</span>
                      </div>
                    </div>
                  </div>

                  {/* Global Rating Scale */}
                  <div className="border border-gray-200 rounded-lg p-5 bg-gradient-to-r from-gray-50 to-gray-100">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="flex items-center justify-center w-8 h-8 bg-gray-600 text-white rounded-full text-sm font-bold">
                        G
                      </div>
                      <div>
                        <h4 className="text-lg font-semibold text-gray-900">Global Rating Scale (GRS)</h4>
                        <p className="text-sm text-gray-600">How confident am I that I received an accurate picture of the patient?</p>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-start gap-3 p-3 bg-white rounded border-l-4 border-red-400">
                        <span className="flex-shrink-0 w-6 h-6 bg-red-100 text-red-800 rounded-full text-xs font-bold flex items-center justify-center">0</span>
                        <span className="text-sm text-gray-700">Not at all confident</span>
                      </div>
                      <div className="flex items-start gap-3 p-3 bg-white rounded border-l-4 border-orange-400">
                        <span className="flex-shrink-0 w-6 h-6 bg-orange-100 text-orange-800 rounded-full text-xs font-bold flex items-center justify-center">1</span>
                        <span className="text-sm text-gray-700">I am confident but required extended questioning on several aspects</span>
                      </div>
                      <div className="flex items-start gap-3 p-3 bg-white rounded border-l-4 border-yellow-400">
                        <span className="flex-shrink-0 w-6 h-6 bg-yellow-100 text-yellow-800 rounded-full text-xs font-bold flex items-center justify-center">2</span>
                        <span className="text-sm text-gray-700">I am confident but required some further questioning</span>
                      </div>
                      <div className="flex items-start gap-3 p-3 bg-white rounded border-l-4 border-green-400">
                        <span className="flex-shrink-0 w-6 h-6 bg-green-100 text-green-800 rounded-full text-xs font-bold flex items-center justify-center">3</span>
                        <span className="text-sm text-gray-700">I am confident and required little or no questioning</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-start gap-3">
                    <Info size={16} className="text-blue-600 mt-0.5" />
                    <div className="text-sm text-blue-800">
                      <div className="font-medium mb-1">Assessment Guide:</div>
                      <ul className="space-y-1 text-xs">
                        <li>• <strong>Score 0:</strong> Not performed competently - needs significant improvement</li>
                        <li>• <strong>Score 1:</strong> Able to perform under firm direction - requires extensive support</li>
                        <li>• <strong>Score 2:</strong> Able to perform under modest direction - developing competency</li>
                        <li>• <strong>Score 3:</strong> Able to perform under minimal direction - demonstrates proficiency</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="border-t border-gray-200 p-4 bg-gray-50 flex-shrink-0">
              <div className="flex justify-between">
                <Button
                  onClick={() => {
                    skipGuide();
                    setShowISBARGuide(false);
                  }}
                  variant="ghost"
                  size="sm"
                  className="px-4 py-2 text-gray-600 hover:text-gray-800"
                >
                  Skip & Don't Show Again
                </Button>
                <Button
                  onClick={() => setShowISBARGuide(false)}
                  variant="outline"
                  size="sm"
                  className="px-4 py-2"
                >
                  Close
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* AI Recommendations Panel */}
      {renderAIPanel()}
    </div>
  );
};

export default SessionDetailMain; 